import { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { auth, db, onAuthStateChanged, signOut, deleteUser, ensureAnonymousAuth, reauthenticateWithCredential, reauthenticateWithPopup, GoogleAuthProvider, OAuthProvider, googleProvider, appleProvider } from './firebase'
import { Capacitor } from '@capacitor/core'
import { doc, deleteDoc, updateDoc, serverTimestamp } from 'firebase/firestore'
import SignIn from './components/SignIn'
import { loadOrCreateUserDoc, syncTicketsToFirestore, syncStreakToFirestore, syncThemeToFirestore, syncUsernameToFirestore, syncAvatarToFirestore, syncTopPicksToFirestore, subscribeToUserTickets, syncDailyCompletionToFirestore, syncMissingDailiesToFirestore, syncPermUnlockedThemesToFirestore, syncThemePurchaseToFirestore, ADMIN_EMAILS } from './utils/userSync'
import { getActorMovies, getActorMovieIds, getActorTVShows, getMovieCast, getMovieCastFull, profileUrl, posterUrl } from './api/tmdb'
import { getOrGenerateTodaysPuzzle, getPuzzleForDate, getPuzzleNumber, getLocalDateStr } from './utils/dailyPuzzleGen'
import {
  getTickets, persistTickets,
  getDailyCompletionForDate, saveDailyCompletion, getDailyCompletions, mergeDailyCompletions,
  getStreak, updateStreak, persistStreak,
  getPermUnlockedThemes, persistPermUnlockedThemes,
  addGameHistory,
  getUser, saveUser,
  getSettings, saveSettings,
  migrateMovesToHops,
} from './utils/storage'
import {
  createSavedGame, updateSavedGame, removeSavedGame,
  getSavedGames, cleanExpiredGames, getSavedGameById, refundTicket,
} from './utils/savedGamesService'
import FlipMovieCard from './components/FlipMovieCard'
import FlipActorCard from './components/FlipActorCard'
import CardOverlay from './components/CardOverlay'
import Filmstrip from './components/Filmstrip'
import WinScreen from './components/WinScreen'
import SetupScreen from './components/SetupScreen'
import HomeScreen from './components/HomeScreen'
import DailyChallenge from './components/DailyChallenge'
import FilmStripReview from './components/FilmStripReview'
import StatsScreen from './components/StatsScreen'
import HowToPlay from './components/HowToPlay'
import TicketStore from './components/TicketStore'
import { identifyUser as identifyRevenueCatUser } from './services/revenueCat'
import HistoricalPuzzles from './components/HistoricalPuzzles'
import GlobalHeader from './components/GlobalHeader'
import SideMenu from './components/SideMenu'
import SettingsScreen from './components/SettingsScreen'
import ProfileScreen from './components/ProfileScreen'
import TermsPage from './components/TermsPage'
import PrivacyPage from './components/PrivacyPage'
import SupportPage from './components/SupportPage'
import Multiplayer from './components/Multiplayer'
import MultiplayerLobby from './components/MultiplayerLobby'
import H2HActorPicker from './components/H2HActorPicker'
import H2HGetReady from './components/H2HGetReady'
import H2HPersonalResults from './components/H2HPersonalResults'
import H2HResultsRoom from './components/H2HResultsRoom'
import CoopGameplay from './components/CoopGameplay'
import SinglePlayer from './components/SinglePlayer'
import { IconTicket, IconClapper, IconCut, IconArrowRight, IconSave, IconTrophy, IconSwords, IconX, IconCueCard, IconCheck, IconTarget, IconFlame, IconThermometer, IconSnowflake, IconMasks } from './components/icons'
import ActorPicker from './components/ActorPicker'
import ChallengeSent from './components/ChallengeSent'
import { SaveExitModal } from './components/SaveGameDialogs'
import H2HReveal from './components/H2HReveal'
import H2HWaiting from './components/H2HWaiting'
import H2HResults from './components/H2HResults'
import { useH2HSync } from './hooks/useH2HSync'
import {
  generateGameCode, getChallenge, saveChallenge, updateChallenge, actorSnapshot,
} from './utils/challenges'
import { createGame, joinGame, deleteGame, getGame, submitPlayerResult, abandonGame, subscribeToGame, incrementCueCardUsed } from './utils/firebaseGame'
import { createParty, joinParty, subscribeToParty, updateParty, assignDirector, assignActorPickers, assignH2HActorPickers, deleteParty, findPartyByCode, leaveParty, endPartyFromLobby, findActivePartyForUser, initiateStart, confirmPlayerStart, getPartyFromServer, resyncFirestore } from './utils/partyGame'
import { App as CapacitorApp } from '@capacitor/app'
import PartyModeSelect from './components/party/PartyModeSelect'
import PartyLobby from './components/party/PartyLobby'
import PartyPlayerScreen from './components/party/PartyPlayerScreen'
import PartyDirectorReveal from './components/party/PartyDirectorReveal'
import PartyTeamSetup from './components/party/PartyTeamSetup'
import PartyTeamBuilder from './components/party/PartyTeamBuilder'
import PartyH2HGameplay from './components/party/PartyH2HGameplay'
import PartyH2HDone from './components/party/PartyH2HDone'
import PartyDCDirectorSelect from './components/party/PartyDCDirectorSelect'
import PartyDCCasting from './components/party/PartyDCCasting'
import PartyDCGameplay from './components/party/PartyDCGameplay'
import PartyDCDone from './components/party/PartyDCDone'
import PartyInstructions from './components/party/PartyInstructions'
import PartyActorPick from './components/party/PartyActorPick'
import PartyGamePlaceholder from './components/party/PartyGamePlaceholder'
import PartyBattleScreen from './components/party/PartyBattleScreen'
import PartyBattleDone from './components/party/PartyBattleDone'
import PartyTrilogyGameplay from './components/party/PartyTrilogyGameplay'
import PartyReactionTest from './components/party/PartyReactionTest'
import PartyCoopGameplay from './components/party/PartyCoopGameplay'
import PartyCoopDone from './components/party/PartyCoopDone'
import {
  THEMES, getSavedTheme, persistTheme,
  getUnlockedThemeIds, getSavedUnlockedThemes, persistUnlockedThemes,
  getNextLockedThemeId, backfillPermFromStreak, THEME_PRICE_TICKETS,
  isAdminUser,
} from './utils/themes'
import './App.css'

/**
 * Delete the current Firebase Auth user IFF it's an anonymous guest — cleans up
 * the throwaway identity that ensureAnonymousAuth() creates when a guest joins a
 * party, so it doesn't linger in Firebase Auth after they leave. Real (Google/
 * Apple) accounts are NEVER touched (guarded on isAnonymous === true). Best-effort
 * and non-fatal: a failed delete just leaves the guest account (prior behaviour)
 * and never blocks the exit. Auth-only — no Firestore writes.
 */
function deleteAnonymousGuest() {
  const u = auth.currentUser
  if (!u || u.isAnonymous !== true) return
  deleteUser(u).catch((err) => {
    // eslint-disable-next-line no-console
    console.warn('[auth] anonymous guest cleanup failed (non-fatal):', err?.code || err)
  })
}

// Tiny inline poster placeholder for revealed-hint thumbs (no network round-trip).
const POSTER_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278'%3E%3Crect fill='%23222' width='185' height='278'/%3E%3C/svg%3E"

// One revealed-hint thumbnail in the Cue Card tray. Tapping calls onView, which
// re-opens that hint's result sheet (free, no spoiler gate).
function CueHintThumb({ hint, onView }) {
  const movie = hint.movie || {}
  return (
    <button type="button" className="cue-thumb" onClick={onView} title={movie.title ? `Re-view: ${movie.title}` : 'Re-view hint'}>
      <div className="cue-thumb__poster">
        <img
          src={movie.poster_path ? posterUrl(movie.poster_path, 'w185') : POSTER_PH}
          alt={movie.title || 'Revealed hint'}
          onError={(e) => { e.currentTarget.src = POSTER_PH }}
        />
        <span className="cue-thumb__tick"><IconCheck size={10} /></span>
      </div>
      <div className="cue-thumb__title">{movie.title}</div>
    </button>
  )
}

/** Map a Firestore party status to the local gameState for session recovery */
function mapPartyStatusToGameState(status, isHost) {
  switch (status) {
    case 'lobby':           return isHost ? 'party-lobby' : 'party-player-lobby'
    case 'pre-start':       return isHost ? 'party-lobby' : 'party-player-lobby'
    case 'director_reveal': return isHost ? 'party-director-reveal' : 'party-player-lobby'
    case 'team_setup':      return isHost ? 'party-team-setup' : 'party-player-lobby'
    case 'team_locked':     return isHost ? 'party-team-setup' : 'party-player-lobby'
    case 'h2h-playing':     return isHost ? 'party-h2h-playing' : 'party-player-lobby'
    case 'h2h-results':     return isHost ? 'party-h2h-results' : 'party-player-lobby'
    case 'director_selection': return isHost ? 'party-dc-director-select' : 'party-player-lobby'
    case 'casting':         return isHost ? 'party-dc-casting' : 'party-player-lobby'
    case 'dc-playing':      return isHost ? 'party-dc-playing' : 'party-player-lobby'
    case 'dc-results':      return isHost ? 'party-dc-results' : 'party-player-lobby'
    case 'actor_pick':      return isHost ? 'party-actor-pick' : 'party-player-lobby'
    case 'reveal':          return isHost ? 'party-actor-pick' : 'party-player-lobby'
    case 'instructions':    return isHost ? 'party-instructions' : 'party-player-lobby'
    case 'countdown':       return isHost ? 'party-actor-pick' : 'party-player-lobby'
    case 'playing':                  return isHost ? 'party-playing' : 'party-player-lobby'
    case 'results':                  return isHost ? 'party-playing' : 'party-player-lobby'
    case 'trilogy-playing':  return isHost ? 'party-trilogy-playing' : 'party-player-lobby'
    case 'round_standings':  return isHost ? 'party-trilogy-playing' : 'party-player-lobby'
    case 'final_standings':  return isHost ? 'party-trilogy-playing' : 'party-player-lobby'
    case 'reaction_test':   return isHost ? 'party-reaction-test' : 'party-player-lobby'
    case 'co-op-playing':   return isHost ? 'party-coop-playing' : 'party-player-lobby'
    case 'coop-results':    return isHost ? 'party-coop-results' : 'party-player-lobby'
    default: return null
  }
}

// Module-scope flag: set to true any time clearAllLocalStorage() runs during
// this session (e.g. uid mismatch on sign-in, sign-out, account deletion).
// The streak-reset diagnostic write uses this to distinguish "stale localStorage
// was wiped before Firestore restore landed" from "data was intact but the
// dates still didn't line up." Resets on full page reload.
let freshWipeThisSession = false

/** Remove every action_ key from localStorage */
function clearAllLocalStorage() {
  freshWipeThisSession = true
  const keys = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (k && k.startsWith('action_')) keys.push(k)
  }
  keys.forEach((k) => localStorage.removeItem(k))
}

/** True when a re-auth error looks like the user dismissing the sign-in sheet
 *  (web popup close OR native Apple/Google cancel) rather than a real failure. */
function isReauthCancel(err) {
  const code = err?.code || ''
  const msg = (err?.message || '').toLowerCase()
  return code === 'auth/popup-closed-by-user'
      || code === 'auth/cancelled-popup-request'
      || code === 'auth/popup-blocked'
      || code === 'auth/user-cancelled'
      || msg.includes('cancel')   // native plugin cancel messages vary by platform
}

const PLACEHOLDER_ACTOR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

const formatTime = (secs) => {
  const m = Math.floor(secs / 60).toString().padStart(2, '0')
  const s = (secs % 60).toString().padStart(2, '0')
  return `${m}:${s}`
}

// gameState: 'home' | 'setup' | 'playing' | 'won' | 'daily' | 'film-review' | 'stats' | 'historical' | 'settings'
//
// Whitelist of gameStates allowed as the *initial* render value on page load.
// Party screens are deliberately excluded — only the session-recovery effect
// (after Firestore data is confirmed) may navigate to a party-* gameState.
const VALID_INITIAL_GAMESTATES = new Set([
  'home', 'playing', 'won', 'daily', 'single-player', 'multiplayer', 'settings', 'stats', 'how-to-play', 'profile',
])

function sanitizeInitialGameState(candidate) {
  return VALID_INITIAL_GAMESTATES.has(candidate) ? candidate : 'home'
}

export default function App() {
  // eslint-disable-next-line no-console
  console.log('[App] render start')
  // Always sanitize the initial gameState. Any party-* or otherwise unknown
  // value is forced to 'home' so we never render a party screen with no data.
  const [gameState, setGameState] = useState(() => sanitizeInitialGameState('home'))
  const [menuOpen, setMenuOpen] = useState(false)
  const [currentUser, _setCurrentUserRaw] = useState(null)  // Firebase user object (use setCurrentUser below)

  // Single chokepoint for every `setCurrentUser` call. Two responsibilities:
  //   1) Force null when this window is the host popup (?host=1), regardless
  //      of what the caller passes. Belt-and-suspenders against any path
  //      (auth callback, sign-in modal, future code) trying to set a real user.
  //   2) Log every set with a stack trace so we can see who called and what
  //      they tried to set — invaluable for diagnosing future regressions.
  const setCurrentUser = useCallback((nextUser) => {
    let hostNow = false
    try { hostNow = new URLSearchParams(window.location.search).get('host') === '1' }
    catch { hostNow = false }
    if (hostNow && nextUser !== null) {
      // eslint-disable-next-line no-console
      console.warn('[currentUser] BLOCKED set in host popup — forcing null', {
        attemptedUid: nextUser?.uid,
        stack: new Error().stack,
      })
      _setCurrentUserRaw(null)
      return
    }
    // eslint-disable-next-line no-console
    console.log('[currentUser] set', nextUser ? { uid: nextUser.uid } : null)
    _setCurrentUserRaw(nextUser)
  }, [])

  const [authReady, setAuthReady] = useState(false)          // true after first onAuthStateChanged callback
  const currentUserRef = useRef(null)                        // always-current ref for use inside callbacks

  // ── Party Mode state ─────────────────────────────────────────────────────
  // Declared at the top of App so that every effect below (auth listener,
  // session recovery, refund effect, etc.) can safely reference these
  // identifiers without hitting a TDZ. Always start null on first render —
  // the session-recovery effect is the ONLY path that may populate them
  // on app load.
  const [partyId, setPartyId] = useState(() => null)
  const [partyCode, setPartyCode] = useState(() => null)
  const [partyRole, setPartyRole] = useState(() => null) // 'host' | 'player'
  const [partyData, setPartyData] = useState(() => null)
  const [partyMyUid, setPartyMyUid] = useState(() => null) // stable uid for this party session
  const partyUnsubRef = useRef(null)
  const partyTicketChargedRef = useRef(false)
  const partyRefundedRef = useRef(false)
  const initialScrubDoneRef = useRef(false)
  // Where to return from the How to Play page — set by whichever surface
  // opened it (settings / side menu / etc).
  const howToPlayReturnRef = useRef('home')
  // Where to return from legal pages (Terms / Privacy / Support).
  const legalReturnRef = useRef('home')
  // Host-exit confirmation: open dialog + pending nav action to run on confirm
  const [hostExitConfirmOpen, setHostExitConfirmOpen] = useState(false)
  const pendingHostExitRef = useRef(null)

  // ── Initial-render scrub ──────────────────────────────────────────────────
  // Runs synchronously before the first paint. Belt-and-suspenders: if anything
  // (HMR, dev tooling, future code) ever leaves a party-* value in gameState or
  // a non-null partyId on first mount, we clear it here BEFORE the first render
  // commits — guaranteeing the recovery effect is the only path that can route
  // into a party screen.
  useLayoutEffect(() => {
    if (initialScrubDoneRef.current) return
    initialScrubDoneRef.current = true
    // eslint-disable-next-line no-console
    console.log('[scrub] initial state on mount:', { gameState, partyId, partyCode, partyRole, hasPartyData: !!partyData, partyMyUid })
    if (!VALID_INITIAL_GAMESTATES.has(gameState)) {
      // eslint-disable-next-line no-console
      console.warn('[scrub] forcing gameState back to home from:', gameState)
      setGameState('home')
    }
    if (partyId !== null) setPartyId(null)
    if (partyCode !== null) setPartyCode(null)
    if (partyRole !== null) setPartyRole(null)
    if (partyData !== null) setPartyData(null)
    if (partyMyUid !== null) setPartyMyUid(null)

    // Popup host signal: if this window was opened with ?host=1, route directly
    // to the party mode select screen as a fresh guest host. This overrides the
    // 'home' scrub above (last setState in the effect wins).
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.get('host') === '1') {
        setGameState('party-mode-select')
      }
    } catch {}
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [signInOpen, setSignInOpen] = useState(false)
  const [signInMessage, setSignInMessage] = useState(null)   // optional contextual message
  const [user, setUser] = useState(() => getUser())
  const [settings, setSettings] = useState(() => getSettings())
  // Remount key for ProfileScreen. Bumped to the signed-in uid only AFTER
  // handleUserSignedIn merges this user's daily completions into localStorage,
  // so the remount re-reads populated data instead of the just-cleared store.
  const [statsKey, setStatsKey] = useState('init')

  const updateUser = useCallback((patch) => {
    const next = saveUser(patch)
    setUser(next)
    // Keep Firestore in sync when the username changes
    if (patch.username !== undefined && currentUser?.uid) {
      syncUsernameToFirestore(currentUser.uid, patch.username ?? null).catch(() => {})
    }
    // Persist avatar changes — but only for custom (non-Google) avatars.
    // Google photo URLs are display-only and must never overwrite a custom avatar
    // in localStorage or Firestore.
    if (patch.avatar !== undefined) {
      const isGoogleUrl = typeof patch.avatar === 'string' && patch.avatar.includes('googleusercontent.com')
      if (!isGoogleUrl) {
        try {
          if (patch.avatar) {
            localStorage.setItem('action_avatar', patch.avatar)
          } else {
            localStorage.removeItem('action_avatar')
          }
        } catch {}
        if (currentUser?.uid) {
          syncAvatarToFirestore(currentUser.uid, patch.avatar ?? null).catch(() => {})
        }
      }
    }
    // Sync Top 3 movies/actors so favorites follow the account across devices.
    // Fire-and-forget — local state/localStorage already updated above; the
    // network write must never block the UI. syncTopPicksToFirestore handles
    // its own errors internally.
    if ((patch.topMovies !== undefined || patch.topActors !== undefined) && currentUser?.uid) {
      syncTopPicksToFirestore(currentUser.uid, {
        topMovies: next.topMovies ?? [],
        topActors: next.topActors ?? [],
      })
    }
    return next
  }, [currentUser]) // eslint-disable-line react-hooks/exhaustive-deps
  const updateSettings = useCallback((patch) => setSettings(saveSettings(patch)), [])

  const handleUserSignedIn = useCallback(async (firebaseUser) => {
    // Belt-and-suspenders: even if something tries to call this in a host popup,
    // bail before any user data is loaded.
    try {
      if (new URLSearchParams(window.location.search).get('host') === '1') {
        // eslint-disable-next-line no-console
        console.log('[auth] handleUserSignedIn blocked — host popup window')
        return
      }
    } catch {}

    // ── User-change detection ──────────────────────────────────────────────
    // If a DIFFERENT user (or a guest, who leaves no record) was last active
    // on this device, wipe their localStorage BEFORE loading this user's data.
    // Without this, the previous user's daily completions / streak / settings
    // bleed into the new account — and worse, the daily-completion sync below
    // would upload that stale data into the new user's Firestore doc.
    //
    // Same-user re-sign-in (e.g. a page refresh) is detected by a matching uid
    // and skips the clear, so the user's local data survives the refresh.
    //
    // 'action_last_uid' is itself an action_* key, so a normal sign-out wipes
    // it — that's fine: the next sign-in then sees null != uid and clears
    // again (a harmless no-op on already-empty storage) before re-hydrating
    // everything from Firestore.
    try {
      const lastUid = localStorage.getItem('action_last_uid')
      if (lastUid !== firebaseUser.uid) {
        // eslint-disable-next-line no-console
        console.log('[auth] user changed — clearing stale localStorage', { lastUid, newUid: firebaseUser.uid })
        clearAllLocalStorage()
      }
      localStorage.setItem('action_last_uid', firebaseUser.uid)
    } catch {}

    try {
      const { data } = await loadOrCreateUserDoc(firebaseUser)
      if (data.tickets != null) setTickets(data.tickets)
      // Restore the FULL streak from Firestore — current streak, longest, AND
      // lastPlayedDate. Merge intelligently: if localStorage has a MORE RECENT
      // lastPlayedDate than Firestore (e.g. a syncStreakToFirestore write failed
      // silently), keep the local date so we don't wipe out a streak that
      // legitimately continued. Firestore wins only when it's equal or newer.
      if (typeof data.streak === 'number') {
        const firestoreStreak = {
          currentStreak: data.streak,
          longestStreak: typeof data.streakLongest === 'number'
            ? Math.max(data.streakLongest, data.streak)
            : data.streak,
          lastPlayedDate: data.streakLastPlayed ?? null,
        }
        const localStreak = getStreak()
        const localDate = localStreak.lastPlayedDate
        const fsDate    = firestoreStreak.lastPlayedDate
        // If local has a newer date, it means a previous syncStreakToFirestore
        // failed — keep local progress rather than overwriting it with stale
        // Firestore data.
        const useLocal  = !!(localDate && fsDate && localDate > fsDate)
        const restoredStreak = useLocal
          ? {
              ...localStreak,
              // Still honour the highest longestStreak across both sources.
              longestStreak: Math.max(localStreak.longestStreak ?? 0, firestoreStreak.longestStreak),
            }
          : firestoreStreak
        console.log(
          '[streak:restore]',
          { localDate, fsDate, useLocal, restoredStreak },
        )
        setStreak(restoredStreak)
        // Persist to localStorage so updateStreak() — which reads localStorage
        // — sees the correct lastPlayedDate when today's daily is solved.
        persistStreak(restoredStreak)
      }
      if (data.username) updateUser({ username: data.username })
      // data.avatar is already resolved: custom Firestore → custom localStorage → Google fallback.
      // updateUser's internal guard ensures Google URLs never overwrite action_avatar or Firestore.
      updateUser({ avatar: data.avatar ?? null })
      // Hydrate Top 3 movies/actors from Firestore so a fresh device shows the
      // user's picks. `?? []` keeps existing users (no such field yet) safe.
      {
        const hydratedMovies = data.topMovies ?? []
        const hydratedActors = data.topActors ?? []
        if (import.meta.env.DEV) console.log(`[topPicksSync] hydrated topMovies: ${hydratedMovies.length}, topActors: ${hydratedActors.length} from Firestore`)
        updateUser({ topMovies: hydratedMovies, topActors: hydratedActors })
      }
      if (data.theme) {
        setActiveTheme(data.theme)
        persistTheme(data.theme)
        document.documentElement.setAttribute('data-theme', data.theme)
      }
      // Hydrate the permanently-unlocked themes list. Union: Firestore + local
      // so offline unlocks earned on this device aren't lost when Firestore
      // hasn't seen them yet. If the merged set is empty (or just baseline)
      // AND the streak is ≥ 7, run the one-time backfill so existing users
      // whose themes were derived from streak under the OLD logic don't
      // suddenly lose visible access. The 'extra' baseline is guaranteed.
      {
        const fsPerm    = Array.isArray(data.permUnlockedThemes) ? data.permUnlockedThemes : []
        const localPerm = getPermUnlockedThemes()
        const unioned   = Array.from(new Set([...fsPerm, ...localPerm, 'extra']))
        // Use the restored streak (just set above) for the backfill check.
        const streakForBackfill = typeof data.streak === 'number' ? data.streak : (getStreak()?.currentStreak ?? 0)
        const merged = backfillPermFromStreak(unioned, streakForBackfill)
        setPermanentlyUnlockedThemes(merged)
        persistPermUnlockedThemes(merged)
        // If the merged set differs from what Firestore has, push it up so
        // Firestore catches the union/backfill on next sign-in.
        const fsSorted     = [...fsPerm].sort().join(',')
        const mergedSorted = [...merged].sort().join(',')
        if (fsSorted !== mergedSorted) {
          syncPermUnlockedThemesToFirestore(firebaseUser.uid, merged).catch(() => {})
        }
      }
      // Admin allowlist — both emails get isAdmin and all themes unlocked.
      const isAdminAccount = data.isAdmin || (data.email && ADMIN_EMAILS.has(data.email.toLowerCase()))
      if (isAdminAccount) {
        setIsAdmin(true)
        localStorage.setItem('action_admin', 'true')
        const allIds = THEMES.map(t => t.id)
        setUnlockedThemes(allIds)
        persistUnlockedThemes(allIds)
      }
      // Two-way daily-completion sync.
      //   1) Pull Firestore completedDailies into localStorage (Firestore wins).
      //   2) Push any localStorage-only completions back up (covers offline plays).
      try {
        if (data.completedDailies && typeof data.completedDailies === 'object') {
          mergeDailyCompletions(data.completedDailies)
        }
        const localAll = getDailyCompletions()
        syncMissingDailiesToFirestore(
          firebaseUser.uid,
          localAll,
          data.completedDailies || {},
        ).catch(() => {})
      } catch (e) {
        console.error('[daily-sync] failed:', e)
      }
      // localStorage now holds THIS user's merged completions — bump the remount
      // key so ProfileScreen re-mounts and re-reads the populated data. Keyed on
      // firebaseUser (the argument), NOT currentUser: this empty-deps callback's
      // closure captures a stale, null currentUser.
      setStatsKey(firebaseUser?.uid ?? 'guest')
      setSignInOpen(false)
    } catch (err) {
      console.error('[Auth] handleUserSignedIn error:', err)
    }
  }, []) // eslint-disable-line

  // ?host=1 — popup host window is ALWAYS a guest/profileless session,
  // regardless of Firebase auth state. Computed once on mount.
  const isHostPopup = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('host') === '1' }
    catch { return false }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[auth] subscribing to onAuthStateChanged')
    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      // Re-read the host flag DIRECTLY from URLSearchParams inside the callback
      // rather than trusting the memoized closure — defends against any stale
      // closure or timing issue.
      let hostPopupNow = false
      try { hostPopupNow = new URLSearchParams(window.location.search).get('host') === '1' }
      catch { hostPopupNow = false }

      // eslint-disable-next-line no-console
      console.log('[auth] onAuthStateChanged fired — user?', !!firebaseUser, firebaseUser?.uid, 'hostPopup?', hostPopupNow)

      if (hostPopupNow) {
        // Popup host: ignore Firebase auth entirely. Force currentUser to null,
        // do NOT load user data, do NOT sign out (other tabs keep their session).
        setCurrentUser(null)
        setAuthReady(true)
        return
      }
      if (firebaseUser && !firebaseUser.isAnonymous) {
        setCurrentUser(firebaseUser)
        // Tell RevenueCat who this user is so purchases are attributed correctly.
        // No-op in browser; fires Purchases.logIn(uid) on native.
        identifyRevenueCatUser(firebaseUser.uid)
        handleUserSignedIn(firebaseUser)
      } else {
        // Either no user, OR an anonymous "guest" identity that exists only to
        // satisfy the Firestore signedIn() rule for party joins/creates. Keep the
        // app in guest mode — currentUser stays null so every account-only guard
        // (ticket store, theme unlocks, profile sync, session recovery) behaves
        // exactly as before. The anonymous uid is read via ensureAnonymousAuth()
        // at the party call sites; we deliberately do NOT load account data or
        // identify RevenueCat for an anonymous guest.
        setCurrentUser(null)
      }
      // Auth has now resolved — safe for downstream effects (e.g. party recovery)
      setAuthReady(true)
    })
    return () => unsub()
  }, []) // eslint-disable-line

  // One-time migration + startup cleanup
  useEffect(() => {
    migrateMovesToHops()
    cleanExpiredGames()
    refreshSavedGames()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Party session recovery ────────────────────────────────────────────────
  // On app load, check if this user is in an active party. If so, silently
  // restore them to the correct screen. Guarded against early-auth, missing
  // data, slow Firestore, and guest sessions.
  const partyRecoveryDoneRef = useRef(false)
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[recovery] effect fired', { authReady, hasUser: !!currentUser?.uid, partyId, done: partyRecoveryDoneRef.current })
    if (partyRecoveryDoneRef.current) return
    // Guard 1: wait until Firebase Auth has fully resolved
    if (!authReady) { console.log('[recovery] waiting for authReady'); return }
    // Guard 4: guests (no signed-in user) skip recovery entirely
    if (!currentUser?.uid) {
      // eslint-disable-next-line no-console
      console.log('[recovery] guest — skipping recovery')
      partyRecoveryDoneRef.current = true
      return
    }
    // Don't run recovery if we've already entered a party some other way
    if (partyId) return
    partyRecoveryDoneRef.current = true
    // eslint-disable-next-line no-console
    console.log('[recovery] starting findActivePartyForUser for uid:', currentUser.uid)

    let aborted = false
    let timeoutId = null
    let unsubLocal = null

    ;(async () => {
      try {
        const active = await findActivePartyForUser(currentUser.uid)
        // eslint-disable-next-line no-console
        console.log('[recovery] findActivePartyForUser returned:', active ? { id: active.id, status: active.status, mode: active.mode } : null)
        if (aborted) return
        if (!active) return

        // Guard 2: validate party doc has the minimum viable shape before routing
        if (!active.id || !active.status || !active.mode) {
          console.warn('[Party] recovery aborted — incomplete party doc')
          return
        }

        const uid = currentUser.uid
        const isHost = active.hostId === uid

        // Guard 5: skip statuses where recovery would be disruptive or meaningless.
        // pre-start: ticket not yet charged, confirmation prompt shouldn't auto-restore.
        // results (finished/seen): player has done their bit — land on home.
        if (active.status === 'pre-start') {
          console.log('[recovery] skipping — party is in pre-start')
          return
        }
        // Trilogy final results is terminal — never auto-restore into it (belt-and-
        // suspenders with findActivePartyForUser's exclusion); land on home.
        if (active.status === 'final_standings') {
          console.log('[recovery] skipping — party finished (final_standings)')
          return
        }
        if (active.status === 'results' || active.status === 'coop-results' || active.status === 'h2h-results' || active.status === 'dc-results') {
          const myPlayer = active.players?.[uid]
          const myMoves = active.playerMoves?.[uid] ?? 0
          if (myPlayer?.status === 'finished' || myPlayer?.lastSeenResults || (active.status === 'h2h-results' && myMoves === 0)) {
            console.log('[recovery] skipping — player already finished/viewed results / zero-move refund')
            return
          }
        }

        // Guard 3: 5-second timeout — if no Firestore snapshot arrives, abort silently
        let firstSnapReceived = false
        timeoutId = setTimeout(() => {
          if (firstSnapReceived || aborted) return
          aborted = true
          console.warn('[Party] recovery timed out — aborting silently')
          if (unsubLocal) { try { unsubLocal() } catch {} }
          // Clear any partial state and stay on home
          partyUnsubRef.current = null
          setPartyId(null)
          setPartyCode(null)
          setPartyRole(null)
          setPartyMyUid(null)
          setPartyData(null)
        }, 5000)

        unsubLocal = subscribeToParty(active.id, (data) => {
          if (aborted) return
          // Re-validate on snapshot — Firestore might have nulled fields
          if (!data || !data.status || !data.mode) {
            if (!firstSnapReceived) {
              aborted = true
              if (timeoutId) clearTimeout(timeoutId)
              if (unsubLocal) { try { unsubLocal() } catch {} }
              partyUnsubRef.current = null
              setPartyId(null)
              setPartyCode(null)
              setPartyRole(null)
              setPartyMyUid(null)
              setPartyData(null)
              return
            }
            // Mid-session data became invalid (e.g. party deleted) — just update normally
            setPartyData(data)
            return
          }
          // First valid snapshot — commit the recovery and route
          if (!firstSnapReceived) {
            firstSnapReceived = true
            if (timeoutId) clearTimeout(timeoutId)
            partyUnsubRef.current = unsubLocal
            setPartyId(active.id)
            setPartyCode(active.code)
            setPartyRole(isHost ? 'host' : 'player')
            setPartyMyUid(uid)
            setPartyData(data)
            const nextState = mapPartyStatusToGameState(data.status, isHost)
            if (nextState) setGameState(nextState)
          } else {
            setPartyData(data)
          }
        })
      } catch (err) {
        console.error('[Party] recovery failed:', err)
        if (timeoutId) clearTimeout(timeoutId)
        if (unsubLocal) { try { unsubLocal() } catch {} }
      }
    })()
  }, [authReady, currentUser?.uid, partyId])

  // ── Stale-listener watchdog ─────────────────────────────────────────────────
  // Firestore onSnapshot listeners can silently stall on mobile webviews (the OS
  // suspends the connection on background / a network blip) and miss a status flip,
  // stranding a player on a passive screen (e.g. the DC reveal → casting) until an
  // app-wake forces a reconnect. This recovers WITHOUT any interaction, for every
  // mode/transition (not per-screen):
  //   • a 5s proactive SERVER re-read advances the UI to current state even while
  //     the app stays foregrounded (the reproduced "stuck until backgrounded" case);
  //   • on app-foreground / network-return, a network toggle restarts the live
  //     listener so real-time delivery resumes (a one-shot read can't restart it).
  useEffect(() => {
    if (!partyId) return
    let cancelled = false

    const reconcile = async () => {
      try {
        const data = await getPartyFromServer(partyId)
        if (cancelled || !data) return
        setPartyData((prev) => {
          try { if (prev && JSON.stringify(prev) === JSON.stringify(data)) return prev } catch {}
          return data
        })
      } catch { /* offline — the next tick (or an event resync) retries */ }
    }

    // Proactive poll — the fix for "stuck while continuously foregrounded".
    const pollId = setInterval(reconcile, 5000)

    // Event-driven re-heal (infrequent): toggle the network to restart the stale
    // live listener, then pull current state. Debounced + single-flight so overlapping
    // app-state / visibility events don't stack toggles.
    let resyncing = false
    let lastResyncAt = 0
    const resync = async () => {
      if (cancelled || resyncing || Date.now() - lastResyncAt < 3000) return
      resyncing = true
      lastResyncAt = Date.now()
      await resyncFirestore()
      await reconcile()
      resyncing = false
    }
    const onVisible = () => { if (document.visibilityState === 'visible') resync() }

    window.addEventListener('online', resync)
    document.addEventListener('visibilitychange', onVisible)
    let appHandle = null
    let appHandleRemoved = false
    CapacitorApp.addListener('appStateChange', ({ isActive }) => { if (isActive) resync() })
      .then((h) => { if (appHandleRemoved) { try { h.remove() } catch {} } else { appHandle = h } })
      .catch(() => {})

    return () => {
      cancelled = true
      appHandleRemoved = true
      clearInterval(pollId)
      window.removeEventListener('online', resync)
      document.removeEventListener('visibilitychange', onVisible)
      if (appHandle) { try { appHandle.remove() } catch {} }
    }
  }, [partyId])

  // Keep currentUserRef in sync so setTickets can always reach the current uid
  useEffect(() => { currentUserRef.current = currentUser }, [currentUser])

  // Tickets — localStorage is the local cache; Firestore is the source of truth
  const [tickets, _setTickets] = useState(() => getTickets())
  const setTickets = useCallback((updater) => {
    _setTickets((prev) => {
      // Guests have no ticket economy — never mutate a signed-out balance (blocks
      // the daily earn grant and any stray spend from touching a guest's tickets).
      if (!currentUserRef.current?.uid) return prev
      const next = typeof updater === 'function' ? updater(prev) : updater
      persistTickets(next)
      // Sync to Firestore immediately on every local change
      if (currentUserRef.current?.uid) {
        syncTicketsToFirestore(currentUserRef.current.uid, next).catch(() => {})
      }
      return next
    })
  }, [])

  // Real-time listener — keeps tickets in sync when another device makes changes.
  // Uses _setTickets directly (bypasses syncTicketsToFirestore) to avoid a
  // write-back feedback loop: remote change → local update, not local → Firestore.
  useEffect(() => {
    if (!currentUser?.uid) return
    const unsub = subscribeToUserTickets(currentUser.uid, (remoteTickets) => {
      _setTickets((prev) => {
        if (prev === remoteTickets) return prev   // already in sync, no re-render
        persistTickets(remoteTickets)             // keep localStorage cache current
        return remoteTickets
      })
    })
    return () => unsub()
  }, [currentUser?.uid])

  // ── Guest ticket-economy gate ───────────────────────────────────────────────
  // Guests have no ticket economy. `tickets` is a localStorage-backed cache that
  // persists on iOS/Capacitor (unlike a fresh web-incognito window), so a stale
  // signed-in balance must never show or be usable for a signed-out guest. Gate
  // the DISPLAY behind a confirmed real account AND authReady, so nothing flashes
  // before auth resolves. Earning is blocked in setTickets; paid entries route
  // guests to the sign-up sheet.
  const economyActive = authReady && !!currentUser
  const displayTickets = economyActive ? tickets : 0

  // Streak — persisted to localStorage
  const [streak, setStreak] = useState(() => getStreak())

  // Admin flag — seeded from localStorage, authoritative value set from Firestore on sign-in
  const [isAdmin, setIsAdmin] = useState(() => isAdminUser())

  // ── Theme system ─────────────────────────────────────────────────────────
  // Set data-theme synchronously during state initialisation so the body
  // attribute exists before the first paint — prevents a flash of un-themed
  // (black) background on load.
  const [activeTheme, setActiveTheme] = useState(() => {
    const saved = getSavedTheme()
    document.documentElement.setAttribute('data-theme', saved)
    return saved
  })
  const [unlockedThemes, setUnlockedThemes] = useState(() => getSavedUnlockedThemes())

  // ── Permanent unlock economy ────────────────────────────────────────────
  // Under the new system, themes are EARNED (every 7-day streak milestone) or
  // BOUGHT (100 tickets). Once earned, they stay forever — a streak reset no
  // longer removes them. `permanentlyUnlockedThemes` is the source of truth;
  // `unlockedThemes` is just (perm ∪ admin-all) derived from it.
  const [permanentlyUnlockedThemes, setPermanentlyUnlockedThemes] = useState(() => {
    // Initial read: localStorage cache + a one-time backfill for users who
    // earned themes under the OLD derive-from-streak logic (a non-zero streak
    // but no perm list yet). Backfill seeds floor(streak/7) themes in order
    // so they don't lose visible access to themes they already had.
    const fromLocal = getPermUnlockedThemes()
    const local     = getStreak()
    return backfillPermFromStreak(fromLocal, local?.currentStreak ?? 0)
  })

  // Keep body attribute in sync whenever activeTheme changes after mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeTheme)
  }, [activeTheme])

  // Derive the user-facing unlocked list from the permanent set + admin override.
  // No longer recomputes from streak — that path was replaced by the permanent
  // unlock economy (see permanentlyUnlockedThemes above). Admin status still
  // unlocks every theme for testing convenience.
  useEffect(() => {
    const allIds = THEMES.map((t) => t.id)
    const ids = isAdmin
      ? allIds
      : Array.from(new Set([...permanentlyUnlockedThemes, 'extra']))
    setUnlockedThemes(ids)
    persistUnlockedThemes(ids)
    // Guard: if the active theme is no longer available (e.g. admin status
    // toggled off and the user was on an admin-only theme), fall back
    // visually — but do NOT write to localStorage; that's the settings
    // screen's job. Under the new system this almost never fires because
    // perm-unlocks don't shrink.
    if (!ids.includes(activeTheme)) {
      const fallback = 'extra'
      setActiveTheme(fallback)
      document.documentElement.setAttribute('data-theme', fallback)
    }
  }, [permanentlyUnlockedThemes, isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetTheme = useCallback((id) => {
    if (!unlockedThemes.includes(id)) return
    setActiveTheme(id)
    persistTheme(id)
    document.documentElement.setAttribute('data-theme', id)
    if (currentUser) syncThemeToFirestore(currentUser.uid, id).catch(() => {})
  }, [unlockedThemes, currentUser])

  /**
   * Buy a locked theme for THEME_PRICE_TICKETS tickets. If the user can't
   * afford it, routes to the ticket store (matches the existing pattern
   * used by historical puzzles / game-over save dialogs).
   *
   * Ticket count and the permanent-unlock list are written to Firestore in
   * a SINGLE updateDoc so a partial failure can't leave the doc inconsistent
   * (tickets debited but theme not added, or vice versa). Local state is
   * updated regardless — Firestore catches up on the next sign-in if the
   * write fails here.
   */
  const handlePurchaseTheme = useCallback((themeId) => {
    if (!themeId || themeId === 'extra') return
    if (permanentlyUnlockedThemes.includes(themeId)) return    // already owned
    if (!currentUser) { openTicketStore(); return }            // guest → sign-up (openTicketStore gates)
    if (tickets < THEME_PRICE_TICKETS) {
      openTicketStore()
      return
    }
    const nextTickets = tickets - THEME_PRICE_TICKETS
    const nextPerm    = Array.from(new Set([...permanentlyUnlockedThemes, themeId, 'extra']))

    // Local updates first — _setTickets bypasses syncTicketsToFirestore so
    // the combined sync below is the single Firestore write for both fields.
    _setTickets(nextTickets)
    persistTickets(nextTickets)
    setPermanentlyUnlockedThemes(nextPerm)
    persistPermUnlockedThemes(nextPerm)

    if (currentUser) {
      syncThemePurchaseToFirestore(currentUser.uid, nextTickets, nextPerm).catch((err) => {
        console.error('[themes] syncThemePurchaseToFirestore failed — local copy is correct, Firestore stale.', err)
      })
    }
  }, [tickets, permanentlyUnlockedThemes, currentUser]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Reset all React state to fresh-guest defaults (used by sign-out & delete account) */
  const resetAllState = useCallback(() => {
    _setTickets(0)   // guests have no economy — a signed-out user resets to 0, not the sign-up grant
    setUser(getUser())            // re-read from (now empty) localStorage → defaults
    setSettings(getSettings())
    setStreak(getStreak())
    setIsAdmin(false)
    setActiveTheme('extra')
    document.documentElement.setAttribute('data-theme', 'extra')
    setUnlockedThemes(['extra'])
    setPermanentlyUnlockedThemes(['extra'])
    setGameState('home')
    setMenuOpen(false)
  }, [])

  /** Full sign-out: clear storage, reset state, Firebase sign-out */
  const handleSignOut = useCallback(async () => {
    clearAllLocalStorage()
    resetAllState()
    setStatsKey('guest')
    await signOut(auth)
    setCurrentUser(null)
  }, [resetAllState])

  /**
   * Delete account — Apple Guideline 5.1.1(v). Order is deliberate and
   * fail-closed so a failure never leaves a half-deleted state:
   *   (1) RE-AUTHENTICATE — Firebase requires a recent login before deletion.
   *       (Reuses the exact credential construction from SignIn.jsx.)
   *   (2) DELETE THE FIRESTORE DOC while still authenticated. (deleteUser ends
   *       the session, so a client write after it would be unauthenticated and
   *       rejected by the `request.auth.uid == uid` rule — the doc MUST go first.)
   *   (3) DELETE THE AUTH USER (recent-login satisfied by step 1).
   *   (4) Clear the native plugin session, then the existing local reset.
   * Each step aborts on failure; nothing is deleted unless re-auth succeeds.
   */
  const handleDeleteAccount = useCallback(async () => {
    const user = auth.currentUser
    if (!user) return
    const uid = user.uid
    const providerId = user.providerData?.[0]?.providerId

    // (1) Re-authenticate.
    try {
      if (Capacitor.isNativePlatform()) {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication')
        if (providerId === 'apple.com') {
          const r = await FirebaseAuthentication.signInWithApple()
          const cred = new OAuthProvider('apple.com').credential({
            idToken: r.credential?.idToken,
            rawNonce: r.credential?.nonce,
          })
          await reauthenticateWithCredential(user, cred)
        } else {
          const r = await FirebaseAuthentication.signInWithGoogle()
          const cred = GoogleAuthProvider.credential(r.credential?.idToken)
          await reauthenticateWithCredential(user, cred)
        }
      } else {
        const provider = providerId === 'apple.com' ? appleProvider : googleProvider
        await reauthenticateWithPopup(user, provider)
      }
    } catch (err) {
      console.error('[DeleteAccount] re-auth failed:', err?.code, err?.message, err)
      if (err?.code === 'auth/user-mismatch') {
        showToast("Please sign in with the same account you're deleting.")
      } else if (isReauthCancel(err)) {
        showToast('Account deletion cancelled.')
      } else {
        showToast("Couldn't verify your identity. Please try again.")
      }
      return // abort — nothing deleted
    }

    // (2) Delete the Firestore user doc (still authenticated).
    try {
      await deleteDoc(doc(db, 'users', uid))
    } catch (err) {
      console.error('[DeleteAccount] Firestore doc delete failed:', err?.code, err?.message, err)
      showToast('Could not delete your data. Please try again.')
      return // abort before deleteUser — account intact, nothing orphaned
    }

    // (3) Delete the Firebase Auth account.
    try {
      await deleteUser(auth.currentUser)
    } catch (err) {
      console.error('[DeleteAccount] deleteUser failed:', err?.code, err?.message, err)
      showToast('Could not delete your account. Please try again.')
      return
    }

    // (4) Clear the native plugin session, then the existing local reset.
    if (Capacitor.isNativePlatform()) {
      try {
        const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication')
        await FirebaseAuthentication.signOut()
      } catch (err) {
        console.error('[DeleteAccount] native signOut failed (non-fatal):', err)
      }
    }
    clearAllLocalStorage()
    resetAllState()
    setCurrentUser(null)
  }, [resetAllState])

  // Daily / historical challenge state
  const [isDailyChallenge, setIsDailyChallenge] = useState(false)
  const [isHistoricalPuzzle, setIsHistoricalPuzzle] = useState(false)
  const [dailyNumber, setDailyNumber] = useState(null)
  const [dailyStatus, setDailyStatus] = useState('loading')
  const [dailyCompletedData, setDailyCompletedData] = useState(null)
  const [currentDailyDate, setCurrentDailyDate] = useState(null)
  // Where the Daily screen's back button returns to. 'home' for normal entry
  // (home / daily-challenge button); 'single-player' when opened via the
  // "✅ Completed Today" tap on the Single Player screen.
  const [dailyReturnTo, setDailyReturnTo] = useState('home')

  // Historical replay score tracking
  const [historicalPrevBest, setHistoricalPrevBest] = useState(null)
  const [isNewHistoricalBest, setIsNewHistoricalBest] = useState(false)

  // Cue Card hint system
  const [cueCardsUsed, setCueCardsUsed] = useState(0)
  const [cueCardDialogOpen, setCueCardDialogOpen] = useState(false)
  const [cueCardResult, setCueCardResult] = useState(null)   // { type, actor?, movie? } | null
  const [cueCardLoading, setCueCardLoading] = useState(false)
  const cueCardProcessingRef = useRef(false)
  const [goalActorMovies, setGoalActorMovies] = useState([])
  // Per-puzzle log of revealed hints (full hint objects) — drives the confirm-
  // modal tray + the green "used" pill. Reset wherever cueCardsUsed resets.
  const [revealedHints, setRevealedHints] = useState([])

  // H2H forfeit — popup overlay + win-screen badge
  const [h2hForfeitInfo, setH2hForfeitInfo] = useState(null) // null | { forfeitedAt0Hops: bool }
  const [h2hOpponentForfeited, setH2hOpponentForfeited] = useState(false)
  const forfeitHandledRef = useRef(false)

  const [startActor, setStartActor] = useState(null)
  const [endActor, setEndActor] = useState(null)

  // Film-review & stats navigation
  const [reviewData, setReviewData] = useState(null)
  const [statsOrigin, setStatsOrigin] = useState('home')

  // H2H multiplayer
  const [activeChallenge, setActiveChallenge] = useState(null)
  const [h2hRole, setH2hRole] = useState(null) // 'p1' | 'p2'
  const [h2hPickStep, setH2hPickStep] = useState(null) // 'start' | 'goal'
  const [h2hPendingStart, setH2hPendingStart] = useState(null)

  // Firebase lobby / Phase C / Phase D state
  const [h2hLobbyGameId, setH2hLobbyGameId] = useState(null)
  const [h2hLobbyCode, setH2hLobbyCode] = useState(null)
  const [h2hLobbyRole, setH2hLobbyRole] = useState(null) // 'p1' | 'p2'
  const [h2hLobbyGameType, setH2hLobbyGameType] = useState('h2h') // 'h2h' | 'coop'
  const [h2hGetReadyData, setH2hGetReadyData] = useState(null) // { startActor, goalActor }
  const [h2hPersonalResult, setH2hPersonalResult] = useState(null)
  // { hops, timeSeconds, chain, finishedFirst, opponentAlreadyFinished }
  const [h2hOpponentActor, setH2hOpponentActor] = useState(null)
  // The actor the opponent locked in Phase C (for waiting display)
  const [h2hResultsGameData, setH2hResultsGameData] = useState(null)
  // Full Firestore game doc passed to H2HResultsRoom

  // Refund: if the host ends the party before 'playing' (i.e. cancelled), every
  // charged player gets their ticket back. Each device refunds itself.
  // (Party state + refs are declared at the top of this function — see above.)
  useEffect(() => {
    if (!partyData?.id) return
    if (partyRole !== 'player') return
    if (!partyMyUid) return
    if (partyRefundedRef.current) return
    if (partyData.status !== 'ended') return
    if (partyData.endedReason !== 'host_cancelled') return
    if (!(partyData.chargedPlayers || []).includes(partyMyUid)) return
    partyRefundedRef.current = true
    const refunded = refundTicket(1)
    setTickets(refunded)
    showToast('Host cancelled — ticket refunded!')
  }, [partyData?.id, partyData?.status, partyData?.endedReason, partyData?.chargedPlayers, partyMyUid, partyRole])

  // H2H sync state
  const [h2hMyResult, setH2hMyResult] = useState(null)
  const [h2hSyncedChallenge, setH2hSyncedChallenge] = useState(null)
  const [h2hGameCode, setH2hGameCode] = useState(null)

  // ── Multi-game saved games service ───────────────────────────────────────
  // activeSavedGameIdRef tracks the currently-active in-progress game entry.
  // Using a ref (not state) avoids stale-closure race conditions when the id
  // is set and read within the same synchronous call sequence.
  const activeSavedGameIdRef = useRef(null)
  const [savedGames, setSavedGames] = useState(() => {
    cleanExpiredGames()
    return getSavedGames()
  })
  const refreshSavedGames = useCallback(() => setSavedGames(getSavedGames()), [])

  // Save / resume
  const [setupActors, setSetupActors] = useState([null, null]) // tracks actor selection mid-setup
  const [activeTicketCost, setActiveTicketCost] = useState(0)
  const [activeGameType, setActiveGameType] = useState(null) // 'single' | 'multiplayer' | null
  const [pendingExit, setPendingExit] = useState(null) // () => void
  const [saveExitOpen, setSaveExitOpen] = useState(false)
  const [toastMsg, setToastMsg] = useState('')

  // Overlays / dialogs
  const [ticketStoreOpen, setTicketStoreOpen] = useState(false)
  // Quit dialog — separate from the Cue Card hint button
  const [quitDialogOpen, setQuitDialogOpen] = useState(false)

  const [chain, setChain] = useState([])
  const [view, setView] = useState('movies')
  const [items, setItems] = useState([])
  // Current actor's TV credits — searchable bridges. Deliberately NOT shown in
  // the base movie grid; they only surface when the player types a search query.
  const [tvItems, setTvItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedMovie, setSelectedMovie] = useState(null)
  const [movieFilter, setMovieFilter] = useState('')
  const filterRef = useRef(null)
  const [pendingJumpIndex, setPendingJumpIndex] = useState(null)
  const [expandedCard, setExpandedCard] = useState(null)

  // Move counter + timer
  const [moveCount, setMoveCount] = useState(0)
  const [startTime, setStartTime] = useState(null)
  const [elapsed, setElapsed] = useState(0)
  const [finalStats, setFinalStats] = useState(null)

  // Live timer tick
  useEffect(() => {
    if (gameState !== 'playing' || !startTime) return
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [gameState, startTime])

  // Browser-back intercept during active game
  useEffect(() => {
    if (gameState !== 'playing') return
    window.history.pushState({ guarded: true }, '')
    const onPop = () => {
      window.history.pushState({ guarded: true }, '')
      if (activeGameType === 'party-battle') {
        setQuitDialogOpen(true)
      } else {
        setPendingExit(() => () => handleReset('home'))
        setSaveExitOpen(true)
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [gameState, isDailyChallenge, isHistoricalPuzzle, activeGameType])

  // ── Cross-tab H2H sync ────────────────────────────────
  useH2HSync(activeChallenge?.gameId ?? null, setH2hSyncedChallenge)

  useEffect(() => {
    const ch = h2hSyncedChallenge
    if (!ch || !activeChallenge?.gameId) return

    // Update local challenge copy
    setActiveChallenge(ch)

    // Both done → results (catches the case where we're in h2h-waiting and opponent finishes)
    if (ch.player1Result && ch.player2Result) {
      setGameState((prev) => {
        if (prev === 'h2h-waiting' || prev === 'h2h-results') return 'h2h-results'
        return prev
      })
      return
    }

    // P1 sees P2 join (p2StartActor set) while still on code-reveal or h2h-pick
    if (h2hRole === 'p1' && ch.p2StartActor && !ch.player2Result) {
      setGameState((prev) => {
        if (prev === 'h2h-code-reveal' || prev === 'h2h-pick') return 'h2h-reveal'
        return prev
      })
    }
  }, [h2hSyncedChallenge, activeChallenge?.gameId, h2hRole])

  const currentActor = [...chain].reverse().find((item) => item.type === 'actor') || startActor

  // Intercept Ctrl+F / Cmd+F when on the movie grid
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && view === 'movies' && gameState === 'playing') {
        e.preventDefault()
        filterRef.current?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [view, gameState])

  useEffect(() => {
    setMovieFilter('')
  }, [view, items])

  const loadActorMovies = useCallback(async (actorId) => {
    setLoading(true)
    setError(null)
    setView('movies')
    setItems([])
    setTvItems([])
    window.scrollTo(0, 0)
    try {
      const movies = await getActorMovies(actorId)
      setItems(movies)
      // Load this actor's TV credits in the background — they're searchable
      // bridges (the "hidden power move"), so they don't block the movie grid
      // and never appear in it. Normalize `name` → `title`, tag mediaType.
      getActorTVShows(actorId)
        .then((shows) => setTvItems(
          shows.map((s) => ({ ...s, title: s.name, mediaType: 'tv' }))
        ))
        .catch(() => setTvItems([]))
    } catch {
      setError('Failed to load movies. Check your TMDB API key.')
    } finally {
      setLoading(false)
    }
  }, [])

  const handleGameStart = async (actorA, actorB) => {
    setStartActor(actorA)
    setEndActor(actorB)
    setChain([{ ...actorA, type: 'actor' }])
    setMoveCount(0)
    setElapsed(0)
    setFinalStats(null)
    setStartTime(Date.now())
    setCueCardResult(null)
    setCueCardLoading(false)
    setGoalActorMovies([])
    setGameState('playing')

    // Stamp actors onto the active saved game entry (if one exists)
    if (activeSavedGameIdRef.current) {
      updateSavedGame(activeSavedGameIdRef.current, {
        actors: { start: actorSnap(actorA), goal: actorSnap(actorB) },
        status: 'playing',
      })
      refreshSavedGames()
    }

    await loadActorMovies(actorA.id)
    getActorMovies(actorB.id).then(setGoalActorMovies).catch(() => {})
  }

  // ── Save / Resume plumbing ────────────────────────────
  const isActivePaid = (gameState === 'setup' || (gameState === 'playing' && !isDailyChallenge && !isHistoricalPuzzle)) && activeGameType === 'single'
  const isActiveFree = gameState === 'playing' && (isDailyChallenge || isHistoricalPuzzle)

  const showToast = useCallback((msg) => {
    setToastMsg(msg)
    setTimeout(() => setToastMsg(''), 2200)
  }, [])

  const actorSnap = (a) => a ? { id: a.id, name: a.name, profile_path: a.profile_path } : null

  const writeSaveGame = useCallback(() => {
    // In the new multi-game system, saves happen automatically on each hop.
    // writeSaveGame is called by handleSaveExit as a final checkpoint — just
    // flush the latest chain/hops in case the user exits between hops.
    if (activeSavedGameIdRef.current) {
      updateSavedGame(activeSavedGameIdRef.current, { chain, hops: moveCount })
      refreshSavedGames()
    }
  }, [chain, moveCount, refreshSavedGames])

  const guardedNav = useCallback((fn) => {
    if (isActivePaid || isActiveFree) {
      setPendingExit(() => fn)
      setSaveExitOpen(true)
    } else {
      fn()
    }
  }, [isActivePaid, isActiveFree])

  const handleSaveExit = () => {
    writeSaveGame()        // flush latest chain/hops
    setStartTime(null)     // pause timer
    setSaveExitOpen(false)
    const fn = pendingExit
    setPendingExit(null)
    showToast('Game saved! Come back anytime.')
    // activeSavedGameIdRef is cleared in handleReset ONLY if status is
    // completed/abandoned — status is 'playing' here, so the entry is kept.
    if (fn) fn()
    else handleReset('home')
  }

  const handleStayOnSet = () => {
    setSaveExitOpen(false)
    setPendingExit(null)
  }

  /**
   * Resume a specific saved game entry from the multi-game dropdown.
   * Single/Historical → restore React state directly.
   * H2H/Co-Op         → fetch Firestore doc and reconnect.
   */
  const handleResumeGame = async (entry) => {
    if (!entry) return

    if (entry.type === 'single' || entry.type === 'historical') {
      const savedChain = entry.chain ?? []
      const startAct   = entry.actors?.start
      const endAct     = entry.actors?.goal

      // If actors not yet set (game was in setup), go back to setup
      if (!startAct || !endAct) {
        setActiveGameType('single')
        setActiveTicketCost(entry.ticketSpent || 1)
        setSetupActors([null, null])
        activeSavedGameIdRef.current = entry.id
        setGameState('setup')
        return
      }

      setStartActor(startAct)
      setEndActor(endAct)
      setChain(savedChain)
      setMoveCount(entry.hops || 0)
      setElapsed(0)
      setStartTime(Date.now())
      setActiveTicketCost(entry.ticketSpent || 1)
      setActiveGameType(entry.type === 'historical' ? null : 'single')
      setIsDailyChallenge(false)
      setIsHistoricalPuzzle(entry.type === 'historical')
      setFinalStats(null)
      activeSavedGameIdRef.current = entry.id

      setGameState('playing')

      const last = savedChain[savedChain.length - 1]
      if (last?.type === 'actor') {
        await loadActorMovies(last.id)
      } else if (last?.type === 'movie') {
        setView('cast')
        setSelectedMovie(last)
        setLoading(true)
        try {
          const cast = await getMovieCast(last.id)
          setItems(await injectGuaranteedActors(cast, last.id))
        } catch { setError('Failed to load cast.') }
        finally { setLoading(false) }
      } else {
        await loadActorMovies(startAct.id)
      }
      if (endAct?.id) getActorMovies(endAct.id).then(setGoalActorMovies).catch(() => {})
      return
    }

    // ── Multiplayer reconnect ────────────────────────────────────────────
    if (entry.gameId) {
      try {
        const gameDoc = await getGame(entry.gameId)
        if (!gameDoc || gameDoc.status === 'abandoned' || gameDoc.status === 'completed') {
          showToast('This game is no longer available.')
          removeSavedGame(entry.id)
          refreshSavedGames()
          return
        }
        activeSavedGameIdRef.current = entry.id
        setH2hLobbyGameId(entry.gameId)
        setH2hLobbyCode(entry.gameCode)
        setH2hLobbyRole(entry.playerKey)
        setH2hLobbyGameType(entry.type)

        if (entry.status === 'lobby') {
          setGameState('mp-lobby')
        } else if (entry.status === 'picking') {
          setGameState('h2h-actor-pick')
        } else if (entry.status === 'playing') {
          if (entry.type === 'coop') {
            setH2hGetReadyData({
              startActor: entry.actors?.start,
              goalActor:  entry.actors?.goal,
            })
            setGameState('coop-gameplay')
          } else {
            // H2H gameplay
            setH2hRole(entry.playerKey)
            setActiveGameType('multiplayer')
            setActiveTicketCost(0)
            await handleGameStart(entry.actors?.start, entry.actors?.goal)
          }
        }
      } catch (err) {
        console.error('[App] handleResumeGame multiplayer error:', err)
        showToast('Error reconnecting to game.')
      }
    }
  }

  /**
   * Abandon a saved game entry from the dropdown.
   * Stubs the refund, cleans up Firestore if multiplayer, removes the entry.
   */
  const handleAbandonSavedGame = async (entry) => {
    if (entry.refundEligible) {
      const newCount = refundTicket(entry.ticketSpent)
      setTickets(newCount)
      showToast('Ticket refunded!')
    }
    // Clean up Firestore game doc for unstarted / in-progress multiplayer games
    if (entry.gameId && ['lobby', 'picking', 'playing'].includes(entry.status)) {
      try { await deleteGame(entry.gameId) } catch {}
    }
    removeSavedGame(entry.id)
    if (activeSavedGameIdRef.current === entry.id) {
      activeSavedGameIdRef.current = null
    }
    refreshSavedGames()
  }

  const handlePurchase = (amount) => {
    setTickets((t) => t + amount)
  }

  const handleSinglePlayerClick = () => {
    setGameState('single-player')
  }

  const handleConfirmSinglePlayer = () => {
    if (!currentUser) {
      setSignInMessage('Create a free account to unlock all game modes and earn tickets.')
      setSignInOpen(true)
      return
    }
    if (tickets < 1) {
      setTicketStoreOpen(true)
      return
    }
    setTickets((t) => t - 1)
    setActiveGameType('single')
    setActiveTicketCost(1)
    setSetupActors([null, null])
    // Save immediately on ticket spend (actors set later when game actually starts)
    const entry = createSavedGame({ type: 'single', status: 'playing', ticketSpent: 1 })
    activeSavedGameIdRef.current = entry.id
    refreshSavedGames()
    setGameState('setup')
  }

  const handleCueCardClick = () => {
    if (!currentUser) {
      setSignInMessage('Create a free account to unlock all game modes and earn tickets.')
      setSignInOpen(true)
      return
    }
    if (tickets === 0) {
      setTicketStoreOpen(true)
    } else {
      setCueCardDialogOpen(true)
    }
  }

  // Re-open a previously revealed hint for free — no ticket, no spoiler gate.
  const reviewHint = (hint) => {
    setCueCardDialogOpen(false)
    setCueCardResult(hint)
  }

  // Log each revealed hint (for the tray + green pill). Fires when a hint is
  // actually shown — direct, or after the spoiler "Reveal Anyway" — deduped by
  // movie id, so re-viewing from the tray never double-adds.
  useEffect(() => {
    if (cueCardResult?.type === 'hint' && cueCardResult.movie?.id != null) {
      setRevealedHints((prev) =>
        prev.some((h) => h.movie?.id === cueCardResult.movie.id) ? prev : [...prev, cueCardResult],
      )
    }
  }, [cueCardResult])

  const handleConfirmCueCard = async () => {
    if (cueCardProcessingRef.current) return
    cueCardProcessingRef.current = true
    setCueCardDialogOpen(false)
    setTickets((t) => t - 1)
    setCueCardsUsed((h) => h + 1)

    // Persist cueCardsUsed to saved game entry (localStorage)
    if (activeSavedGameIdRef.current) {
      const entry = getSavedGameById(activeSavedGameIdRef.current)
      updateSavedGame(activeSavedGameIdRef.current, {
        cueCardsUsed: (entry?.cueCardsUsed ?? 0) + 1,
      })
    }

    // Persist to Firestore when in an H2H game
    if (h2hLobbyGameId && (h2hLobbyRole ?? h2hRole)) {
      incrementCueCardUsed(h2hLobbyGameId, h2hLobbyRole ?? h2hRole).catch(() => {})
    }

    setCueCardLoading(true)
    setCueCardResult(null)

    // Search depth constants
    const MIN_POPULARITY     = 2   // filter out obscure actors as bridge candidates
    const TOP_MOVIES_CURRENT = 15  // current actor's movies to scan
    const TOP_CAST_PER_MOVIE = 10  // top-billed cast members per movie
    const STAGE2_COSTARS     = 10  // co-stars to expand in Stage 2
    const TOP_MOVIES_COSTAR  = 5   // co-star's movies to scan in Stage 2

    // Helper: does a Set of movie IDs overlap with the goal's movie IDs?
    const overlapsGoal = (movieIdSet, goalIds) => {
      for (const id of goalIds) {
        if (movieIdSet.has(id)) return true
      }
      return false
    }

    try {
      // Use getActorMovieIds for the full (unfiltered) goal movie set
      const goalMovieIds = await getActorMovieIds(endActor.id)
      const curActorMovies = await getActorMovies(currentActor.id)

      // ── Spoiler check: current actor directly co-starred with goal ──
      const directMovie = curActorMovies.find((m) => goalMovieIds.has(m.id))
      if (directMovie) {
        setCueCardResult({
          type: 'spoiler',
          hiddenResult: { type: 'hint', temperature: 'hot', actor: endActor, movie: directMovie },
        })
        return
      }

      // ── Build co-star candidate pool (top 15 movies × top 10 cast) ──
      // endActor is NOT excluded here so it can surface as a spoiler bridge
      const excludeIds = new Set(
        chain.filter((i) => i.type === 'actor').map((i) => i.id)
      )
      if (currentActor?.id) excludeIds.add(currentActor.id)

      const topMovies = curActorMovies.slice(0, TOP_MOVIES_CURRENT)
      const casts = await Promise.all(
        topMovies.map((m) => getMovieCast(m.id).catch(() => []))
      )

      const seenCandidateIds = new Set()
      const candidatePool = []
      for (let mi = 0; mi < casts.length; mi++) {
        const filtered = casts[mi]
          .filter((a) => !excludeIds.has(a.id) && (a.popularity ?? 0) >= MIN_POPULARITY)
          .slice(0, TOP_CAST_PER_MOVIE)
        for (const a of filtered) {
          if (!seenCandidateIds.has(a.id)) {
            seenCandidateIds.add(a.id)
            candidatePool.push({ ...a, _sharedMovie: topMovies[mi] })
          }
        }
      }
      candidatePool.sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0))

      // Spoiler: goal actor IS in the cast pool
      const goalInPool = candidatePool.find((c) => c.id === endActor?.id)
      if (goalInPool) {
        setCueCardResult({
          type: 'spoiler',
          hiddenResult: { type: 'hint', temperature: 'hot', actor: goalInPool, movie: goalInPool._sharedMovie },
        })
        return
      }

      const stage2Pool = candidatePool.slice(0, STAGE2_COSTARS)

      // Helper: expand a single co-star into their unique, popular co-stars
      const expandCoStars = async (coStar, extraExclude = new Set()) => {
        const movies = await getActorMovies(coStar.id)
        const casts = await Promise.all(
          movies.slice(0, TOP_MOVIES_COSTAR).map((m) => getMovieCast(m.id).catch(() => []))
        )
        const seen = new Set([coStar.id, ...excludeIds, ...extraExclude])
        const result = []
        for (const cast of casts) {
          for (const a of cast
            .filter((a) => !seen.has(a.id) && (a.popularity ?? 0) >= MIN_POPULARITY)
            .slice(0, TOP_CAST_PER_MOVIE)) {
            if (!seen.has(a.id)) { seen.add(a.id); result.push(a) }
          }
        }
        result.sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0))
        return result
      }

      // ── Stage 1: 🌡️ Getting Warmer — bridge is 3 hops from goal ──
      // Candidate's co-star's co-star connects to goal.
      // Most popular valid candidate from stage2Pool wins.
      const stage1Results = await Promise.all(
        stage2Pool.map(async (coStar) => {
          try {
            const twoHopActors = (await expandCoStars(coStar)).slice(0, 5)
            for (const twoHop of twoHopActors) {
              const threeHopActors = (await expandCoStars(twoHop, new Set([coStar.id]))).slice(0, 5)
              const threeHopMovieIds = await Promise.all(
                threeHopActors.map((a) => getActorMovieIds(a.id).catch(() => new Set()))
              )
              if (threeHopMovieIds.some((ids) => overlapsGoal(ids, goalMovieIds))) return coStar
            }
            return null
          } catch { return null }
        })
      )
      const gettingWarmerBridge = stage1Results.find(Boolean)
      if (gettingWarmerBridge) {
        setCueCardResult({
          type: 'hint',
          temperature: 'getting-warmer',
          actor: gettingWarmerBridge,
          movie: gettingWarmerBridge._sharedMovie,
        })
        return
      }

      // ── Stage 2: 🥵 Warm — bridge is 2 hops from goal ──
      // Candidate's co-star connects to goal. Cache from Stage 1 makes this cheap.
      const stage2Results = await Promise.all(
        stage2Pool.map(async (coStar) => {
          try {
            const deepCandidates = await expandCoStars(coStar)
            const deepMovieIdSets = await Promise.all(
              deepCandidates.map((a) => getActorMovieIds(a.id).catch(() => new Set()))
            )
            return deepMovieIdSets.some((ids) => overlapsGoal(ids, goalMovieIds)) ? coStar : null
          } catch { return null }
        })
      )
      const warmBridge = stage2Results.find(Boolean)
      if (warmBridge) {
        setCueCardResult({
          type: 'hint',
          temperature: 'warm',
          actor: warmBridge,
          movie: warmBridge._sharedMovie,
        })
        return
      }

      // ── Stage 3: 🔥 Hot — last resort, bridge is 1 hop from goal ──
      // Candidate directly appeared with the goal actor.
      const stage3MovieIdSets = await Promise.all(
        candidatePool.map((c) => getActorMovieIds(c.id).catch(() => new Set()))
      )
      const hotBridge = candidatePool.find((_, i) =>
        overlapsGoal(stage3MovieIdSets[i], goalMovieIds)
      )
      if (hotBridge) {
        setCueCardResult({
          type: 'hint',
          temperature: 'hot',
          actor: hotBridge,
          movie: hotBridge._sharedMovie,
        })
        return
      }

      // ── All stages failed — refund ──
      console.warn('[CueCard] No bridge found between:', currentActor?.name, '→', endActor?.name)
      setTickets((t) => t + 1)
      setCueCardsUsed((h) => Math.max(0, h - 1))
      setCueCardResult({ type: 'none' })
    } catch (err) {
      console.error('[CueCard] Error finding hint:', err)
      setTickets((t) => t + 1)
      setCueCardsUsed((h) => Math.max(0, h - 1))
      setCueCardResult({ type: 'error' })
    } finally {
      setCueCardLoading(false)
      cueCardProcessingRef.current = false
    }
  }

  const handleHistoricalPlay = async (date, number) => {
    if (!currentUser) {
      setSignInMessage('Create a free account to unlock all game modes and earn tickets.')
      setSignInOpen(true)
      return
    }
    if (tickets < 1) { setTicketStoreOpen(true); return }
    const prevBest = getDailyCompletionForDate(date)
    setHistoricalPrevBest(prevBest)
    setIsNewHistoricalBest(false)
    setIsHistoricalPuzzle(true)
    setDailyNumber(number)
    setCurrentDailyDate(date)
    setTickets((t) => t - 1)
    const { startActor: sa, endActor: ea } = await getPuzzleForDate(date)
    // Save immediately — actors known at this point so we set them right away
    const entry = createSavedGame({
      type: 'historical',
      status: 'playing',
      ticketSpent: 1,
      actors: { start: actorSnap(sa), goal: actorSnap(ea) },
    })
    activeSavedGameIdRef.current = entry.id
    refreshSavedGames()
    await handleGameStart(sa, ea)
  }

  // Guarantee that the goal actor (and cue card bridge actor, if applicable) always
  // appear in the cast grid for a given movie, even if billed beyond the 30-actor cap.
  // Replaces the last N slots in the existing list — total count stays exactly the same.
  const injectGuaranteedActors = async (cast, movieId) => {
    const actorsToGuarantee = []
    if (endActor) actorsToGuarantee.push(endActor)
    // Also guarantee the cue card bridge actor in the movie the hint is pointing to
    if (
      cueCardResult?.type === 'hint' &&
      cueCardResult.movie?.id === movieId &&
      cueCardResult.actor
    ) {
      actorsToGuarantee.push(cueCardResult.actor)
    }

    const missing = actorsToGuarantee.filter(
      (a) => a && !cast.some((c) => c.id === a.id)
    )
    if (missing.length === 0) return cast

    const fullCast = await getMovieCastFull(movieId).catch(() => [])
    const toInject = []
    for (const actor of missing) {
      const entry = fullCast.find((c) => c.id === actor.id)
      if (entry && !toInject.some((c) => c.id === entry.id)) toInject.push(entry)
    }
    if (toInject.length === 0) return cast // actor(s) simply not in this movie

    // Replace the last N slots so total count stays unchanged
    const keepCount = Math.max(0, cast.length - toInject.length)
    return [...cast.slice(0, keepCount), ...toInject]
  }

  const handleMovieSelect = async (movie, cast) => {
    // injectGuaranteedActors hits the /movie/{id}/credits endpoint — invalid
    // for a TV bridge, so skip injection and use the TV cast as-is.
    const finalCast = movie.mediaType === 'tv'
      ? cast
      : await injectGuaranteedActors(cast, movie.id)
    setChain((prev) => [...prev, { ...movie, type: 'movie' }])
    setSelectedMovie(movie)
    setItems(finalCast)
    setView('cast')
    window.scrollTo(0, 0)
  }

  // ── Firebase H2H win path (Phase D) ──────────────────

  const handleFirebaseH2HWin = async (finalMoves, finalTime, finalChain) => {
    // Trim chain to only the fields needed for Firestore storage + Phase E display
    const chainForStorage = finalChain.map(item =>
      item.type === 'actor'
        ? { type: 'actor', id: item.id, name: item.name, profile_path: item.profile_path ?? null }
        : { type: 'movie', id: item.id, title: item.title, poster_path: item.poster_path ?? null }
    )

    try {
      const { finishedFirst, status, opponentActor } = await submitPlayerResult(
        h2hLobbyGameId,
        h2hLobbyRole,
        { hops: finalMoves, timeSeconds: finalTime, chain: chainForStorage },
      )

      // Mark saved game completed (removed when results are dismissed via handleReset)
      if (activeSavedGameIdRef.current) {
        updateSavedGame(activeSavedGameIdRef.current, { status: 'completed' })
        refreshSavedGames()
      }

      setH2hPersonalResult({
        hops: finalMoves,
        timeSeconds: finalTime,
        chain: finalChain,          // full in-memory chain for Filmstrip display
        finishedFirst,
        opponentAlreadyFinished: status === 'results',
      })
      setH2hOpponentActor(opponentActor)
      setGameState('h2h-personal-results')
    } catch {
      // Firestore error — fall back to normal win screen so the player isn't stuck
      if (activeSavedGameIdRef.current) {
        updateSavedGame(activeSavedGameIdRef.current, { status: 'completed' })
        refreshSavedGames()
      }
      setGameState('won')
    }
  }

  // Phase E: both players finished — show results room
  const handleH2hBothFinished = (gameData) => {
    setH2hResultsGameData(gameData)
    setGameState('h2h-results-room')
  }

  // Rematch accepted — jump straight to actor pick in the new game
  const handleH2hRematch = (newGameId, myNewRole) => {
    setH2hLobbyGameId(newGameId)
    setH2hLobbyRole(myNewRole)
    setH2hResultsGameData(null)
    setH2hPersonalResult(null)
    setH2hOpponentActor(null)
    setH2hGetReadyData(null)
    setGameState('h2h-actor-pick')
  }

  // Player exits results room to menu
  const handleH2hResultsExit = () => {
    handleReset('multiplayer')
  }

  // ── Actor selection ───────────────────────────────────

  const handleActorSelect = async (actor) => {
    if (actor.id === endActor.id) {
      const finalMoves = moveCount + 1
      const finalTime = Math.floor((Date.now() - startTime) / 1000)
      const finalChain = [...chain, { ...actor, type: 'actor' }]
      const emojiChain = finalChain
        .map((item, i) => {
          if (item.type === 'movie') return '🎥'
          if (i === finalChain.length - 1) return '🏆'
          return item.gender === 1 ? '👩' : '👨'
        })
        .join(' → ')

      if (isDailyChallenge && currentDailyDate) {
        setTickets((t) => t + 1)
        const completionData = {
          puzzleNumber: dailyNumber,
          moves: finalMoves,
          timeSeconds: finalTime,
          time: formatTime(finalTime),
          chain: finalChain,
          emojiChain,
          hintsUsed: cueCardsUsed,
        }
        saveDailyCompletion(currentDailyDate, completionData)
        const newStreak = updateStreak(currentDailyDate)
        setStreak(newStreak)

        // 7-day milestone → award the next still-locked theme. % 7 === 0
        // handles both the first unlock (streak hits 7) and uninterrupted
        // runs (14 → 2nd, 21 → 3rd, …). getNextLockedThemeId walks THEMES
        // in order and returns the lowest-ordered ID NOT yet permanently
        // unlocked — so an out-of-order ticket purchase doesn't break the
        // cadence; a later streak unlock will fill the gap.
        if (newStreak.currentStreak > 0 && newStreak.currentStreak % 7 === 0) {
          const nextId = getNextLockedThemeId(permanentlyUnlockedThemes)
          if (nextId) {
            const updated = Array.from(new Set([...permanentlyUnlockedThemes, nextId, 'extra']))
            setPermanentlyUnlockedThemes(updated)
            persistPermUnlockedThemes(updated)
            if (currentUser) {
              syncPermUnlockedThemesToFirestore(currentUser.uid, updated).catch((err) => {
                console.error('[themes] syncPermUnlockedThemesToFirestore failed — local copy is correct, Firestore stale.', err)
              })
            }
          }
        }

        if (currentUser) {
          syncStreakToFirestore(currentUser.uid, newStreak).catch((err) => {
            console.error('[streak] syncStreakToFirestore failed — streak is saved locally but Firestore is stale. Sign-in restore will prefer local data.', err)
          })
          syncDailyCompletionToFirestore(currentUser.uid, currentDailyDate, completionData).catch(() => {})

          // Diagnostic: if updateStreak just took the reset branch, record
          // what it compared so we can investigate user-reported streak bugs
          // from the Firestore console. Best-effort, fire-and-forget.
          if (newStreak._resetInfo) {
            const debugPayload = {
              streakResetDebug: {
                timestamp:  serverTimestamp(),
                expected:   newStreak._resetInfo.expected,
                got:        newStreak._resetInfo.got,
                freshWipe:  freshWipeThisSession,
              },
            }
            console.warn('[streak:debug] writing streakResetDebug to Firestore', debugPayload.streakResetDebug)
            updateDoc(doc(db, 'users', currentUser.uid), debugPayload)
              .catch((err) => console.error('[streak:debug] Firestore write failed', err))
          }
        }
        setDailyCompletedData(completionData)
      } else if (isHistoricalPuzzle && currentDailyDate) {
        const completionData = {
          puzzleNumber: dailyNumber,
          moves: finalMoves,
          timeSeconds: finalTime,
          time: formatTime(finalTime),
          chain: finalChain,
          emojiChain,
          hintsUsed: cueCardsUsed,
        }
        const prev = historicalPrevBest
        const isBetter = !prev
          || finalMoves < prev.moves
          || (finalMoves === prev.moves && finalTime < (prev.timeSeconds ?? Infinity))
        if (isBetter) {
          saveDailyCompletion(currentDailyDate, completionData)
          if (currentUser) {
            syncDailyCompletionToFirestore(currentUser.uid, currentDailyDate, completionData).catch(() => {})
          }
        }
        setIsNewHistoricalBest(isBetter)
        setDailyCompletedData(completionData)
      }

      addGameHistory({
        isDaily: isDailyChallenge,
        dailyNumber: (isDailyChallenge || isHistoricalPuzzle) ? dailyNumber : null,
        date: new Date().toISOString().slice(0, 10),
        moves: finalMoves,
        timeSeconds: finalTime,
        startActor: startActor?.name,
        endActor: actor.name,
      })

      setChain(finalChain)
      setFinalStats({ moves: finalMoves, time: finalTime })

      // ── Party Battle Royale / Trilogy win path ──
      if ((activeGameType === 'party-battle' || activeGameType === 'party-trilogy') && partyId && partyMyUid) {
        const chainForStorage = finalChain.map(item =>
          item.type === 'actor'
            ? { type: 'actor', id: item.id, name: item.name, profile_path: item.profile_path ?? null }
            : { type: 'movie', id: item.id, title: item.title, poster_path: item.poster_path ?? null }
        )
        updateParty(partyId, {
          [`players.${partyMyUid}.status`]: 'finished',
          [`players.${partyMyUid}.hops`]: finalMoves,
          [`players.${partyMyUid}.time`]: finalTime,
          [`players.${partyMyUid}.chain`]: chainForStorage,
          [`players.${partyMyUid}.finishedAt`]: new Date(),
        }).catch(() => {})
        if (activeGameType === 'party-trilogy') {
          setGameState('party-trilogy-done')
        } else {
          setGameState('party-battle-done')
        }
        return
      }

      // Firebase H2H win path (Phase D) — skip if opponent already forfeited
      if (h2hLobbyGameId && !isDailyChallenge && !isHistoricalPuzzle && !h2hOpponentForfeited) {
        await handleFirebaseH2HWin(finalMoves, finalTime, finalChain)
        return
      }

      // Legacy localStorage H2H win path
      if (activeChallenge?.gameId && !isDailyChallenge && !isHistoricalPuzzle) {
        const result = { moves: finalMoves, timeSeconds: finalTime, chain: finalChain, hintsUsed: cueCardsUsed }
        const resultKey = h2hRole === 'p1' ? 'player1Result' : 'player2Result'
        const opponentKey = h2hRole === 'p1' ? 'player2Result' : 'player1Result'
        const updated = updateChallenge(activeChallenge.gameId, { [resultKey]: result, status: 'in-progress' })
        setH2hMyResult(result)
        if (activeSavedGameIdRef.current) {
          updateSavedGame(activeSavedGameIdRef.current, { status: 'completed' })
          refreshSavedGames()
        }
        if (updated?.[opponentKey]) {
          setActiveChallenge(updated)
          setGameState('h2h-results')
        } else {
          setActiveChallenge(updated)
          setGameState('h2h-waiting')
        }
        return
      }

      // Mark saved game as completed (removed when win screen is dismissed via handleReset)
      if (activeSavedGameIdRef.current) {
        updateSavedGame(activeSavedGameIdRef.current, { status: 'completed' })
        refreshSavedGames()
      }
      setGameState('won')
      return
    }
    const newHops  = moveCount + 1
    const newChain = [...chain, { ...actor, type: 'actor' }]
    setMoveCount(newHops)
    setChain(newChain)
    setCueCardResult(null) // dismiss any open hint when moving to next actor
    // Update saved game on every hop
    if (activeSavedGameIdRef.current) {
      updateSavedGame(activeSavedGameIdRef.current, {
        hops: newHops,
        chain: newChain,
        refundEligible: false, // no refund once hops >= 1
      })
      refreshSavedGames()
    }
    // Write hop progress to Firestore for party battle royale (fire-and-forget)
    if (activeGameType === 'party-battle' && partyId && partyMyUid) {
      updateParty(partyId, {
        [`players.${partyMyUid}.hops`]: newHops,
      }).catch(() => {})
    }
    await loadActorMovies(actor.id)
  }

  // ── Party Mode handlers ───────────────────────────────────────────────────

  const cleanupParty = () => {
    if (partyUnsubRef.current) { partyUnsubRef.current(); partyUnsubRef.current = null }
    partyTicketChargedRef.current = false
    partyRefundedRef.current = false
    setPartyId(null)
    setPartyCode(null)
    setPartyRole(null)
    setPartyData(null)
    setPartyMyUid(null)
    // A guest's anonymous auth identity is throwaway — delete it on every party
    // exit (back-to-home, party ended, leave). No-op for real signed-in accounts.
    // Runs after the party's own Firestore writes (leaveParty / endParty are
    // awaited before cleanupParty in every caller).
    deleteAnonymousGuest()
  }

  // ── Host navigation guard ─────────────────────────────────────────────────
  // The host must confirm before leaving any party host screen.
  // requestHostExit() stores the desired action and opens the confirmation dialog.
  const PARTY_HOST_STATES = useMemo(() => new Set([
    'party-mode-select',
    'party-mode-reselect',
    'party-lobby',
    'party-director-reveal',
    'party-team-setup',
    'party-actor-pick',
    'party-playing',
    'party-battle-done',
    'party-reaction-test',
    'party-coop-playing',
    'party-coop-results',
    'party-h2h-playing',
    'party-h2h-results',
    'party-dc-director-select',
    'party-dc-casting',
    'party-dc-playing',
    'party-dc-results',
    'party-instructions',
    'party-trilogy-playing',
  ]), [])
  const isHostOnPartyScreen = partyRole === 'host' && PARTY_HOST_STATES.has(gameState)

  const requestHostExit = useCallback((action) => {
    pendingHostExitRef.current = action ?? (() => handleResetRef.current?.('home'))
    setHostExitConfirmOpen(true)
  }, [])

  // handleReset is declared later — capture it in a ref so requestHostExit
  // can reference it without hoisting issues.
  const handleResetRef = useRef(null)

  const confirmHostExitAndEnd = useCallback(async () => {
    setHostExitConfirmOpen(false)
    const action = pendingHostExitRef.current
    pendingHostExitRef.current = null
    // End the party in Firestore so all players see it ended
    if (partyId) {
      try {
        if (partyData?.status === 'lobby') {
          await endPartyFromLobby(partyId)
          const idToDelete = partyId
          setTimeout(() => { deleteParty(idToDelete).catch(() => {}) }, 8000)
        } else {
          await updateParty(partyId, { status: 'ended', endedReason: 'host_exit' }).catch(() => {})
        }
      } catch {}
    }
    cleanupParty()
    if (action) action()
    else handleResetRef.current?.('home')
    // Host popup: close the window after the party is fully torn down.
    if (isHostPopup) { try { window.close() } catch {} }
  }, [partyId, partyData?.status, isHostPopup]) // eslint-disable-line react-hooks/exhaustive-deps

  const dismissHostExit = useCallback(() => {
    pendingHostExitRef.current = null
    setHostExitConfirmOpen(false)
  }, [])

  // Round-reset cleanup: every device (host + every player) clears its per-round
  // refs the moment the party returns to 'lobby' status. Without this, the
  // player's partyTicketChargedRef stays true from round N and silently blocks
  // confirmPlayerStart in round N+1's pre-start phase → host sees "0/N confirmed"
  // forever. Fires after Play Again, Back to Lobby, or any other round reset.
  useEffect(() => {
    if (partyData?.status === 'lobby') {
      partyTicketChargedRef.current = false
      partyRefundedRef.current = false
    }
  }, [partyData?.status])

  // Host-side auto-advance for Co-Op status transitions.
  // Co-Op flow: pre-start → reaction_test → actor_pick → reveal → countdown(LCA) → co-op-playing → coop-results
  // Two transitions are written by player devices and the host has to follow them:
  //   1) PartyReactionTest finalizes → writes status: 'actor_pick' + turnOrder + actorPickerIds
  //   2) Active player picks the goal actor → writes status: 'coop-results'
  // Without this effect the host's gameState gets stuck on the previous screen
  // even though partyData.status has advanced via the Firestore listener.
  // (BR/SR don't need this because PartyBattleScreen handles both
  // 'playing' and 'results' internally on the same gameState.)
  useEffect(() => {
    if (partyRole !== 'host') return
    const status = partyData?.status
    if (status === 'actor_pick' && gameState === 'party-reaction-test') {
      setGameState('party-actor-pick')
    } else if (status === 'coop-results' && gameState === 'party-coop-playing') {
      setGameState('party-coop-results')
    } else if (status === 'h2h-results' && gameState === 'party-h2h-playing') {
      setGameState('party-h2h-results')
    } else if (status === 'director_reveal' && gameState === 'party-dc-director-select') {
      // Director's Cut: host stays on the same component (it handles both phases)
      // but we update gameState for consistency with mapPartyStatusToGameState.
      // No-op: the same component renders both director_selection and director_reveal.
    } else if (status === 'casting' && gameState === 'party-dc-director-select') {
      setGameState('party-dc-casting')
    } else if (status === 'reveal' && gameState === 'party-dc-casting' && partyData?.mode === 'directors-cut') {
      setGameState('party-actor-pick')
    } else if (status === 'dc-results' && gameState === 'party-dc-playing') {
      setGameState('party-dc-results')
    } else if (status === 'team_locked' && gameState === 'party-team-setup' && partyData?.mode === 'h2h') {
      // Director locked in teams — host assigns team-aware actor pickers and advances
      const id = partyId
      if (id) {
        assignH2HActorPickers(id)
          .then(() => setGameState('party-actor-pick'))
          .catch(() => {})
      }
    }
  }, [partyRole, partyData?.status, partyData?.mode, gameState, partyId])

  // ── Trilogy player: auto-return to lobby when next round's actor-pick begins ──
  // When a player finishes a Trilogy round they land on 'party-trilogy-done'.
  // The host will eventually bump status to 'actor_pick' for the next round.
  // When that happens, reset game state and drop back to party-player-lobby so
  // they flow through the normal actor-pick → reveal → countdown → playing loop.
  useEffect(() => {
    if (partyRole !== 'player') return
    if (gameState !== 'party-trilogy-done') return
    const ps = partyData?.status
    if (ps !== 'actor_pick' && ps !== 'trilogy-playing' && ps !== 'lobby') return
    // Reset single-round game state
    setChain([])
    setItems([])
    setView('movies')
    setStartActor(null)
    setEndActor(null)
    setMoveCount(0)
    setElapsed(0)
    setFinalStats(null)
    setActiveGameType(null)
    setGameState('party-player-lobby')
  }, [partyRole, gameState, partyData?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Battle Royale / Speed Run / Co-Op / H2H player: auto-return to player
  // lobby when the host transitions past the mode's results screen (Play Again
  // → new game in any mode, or a brand new round). PartyBattleDone /
  // PartyCoopDone / PartyH2HDone each handle three statuses internally:
  //   * the mode's own 'playing' state ("you finished, waiting for others")
  //   * the mode's own '*-results' state (final ranking)
  //   * 'ended' (party closed, "Thanks for playing")
  // For ANY other status (host went back to lobby and started a new game in
  // any mode → status becomes 'pre-start' / 'director_reveal' / 'casting' /
  // 'actor_pick' / etc.), the done screen falls through to the wrong "waiting
  // for N players" fallback. Auto-route to player-lobby so PartyPlayerScreen
  // picks up the new game's flow. Same pattern as the Trilogy auto-return
  // above; mode-specific because each done screen handles a different
  // "still-playing" status name.
  useEffect(() => {
    if (partyRole !== 'player') return
    const ps = partyData?.status
    let shouldStay = false
    if (gameState === 'party-battle-done') {
      shouldStay = ps === 'playing' || ps === 'results' || ps === 'ended'
    } else if (gameState === 'party-coop-results') {
      shouldStay = ps === 'co-op-playing' || ps === 'coop-results' || ps === 'ended'
    } else if (gameState === 'party-h2h-results') {
      shouldStay = ps === 'h2h-playing' || ps === 'h2h-results' || ps === 'ended'
    } else {
      return
    }
    if (shouldStay) return
    // Host advanced — reset per-round state and route the player into the
    // normal PartyPlayerScreen flow.
    setChain([])
    setItems([])
    setView('movies')
    setStartActor(null)
    setEndActor(null)
    setMoveCount(0)
    setElapsed(0)
    setFinalStats(null)
    setActiveGameType(null)
    setGameState('party-player-lobby')
  }, [partyRole, gameState, partyData?.status]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartParty = () => {
    setGameState('party-mode-select')
  }

  const handlePartyModeSelected = async (mode) => {
    try {
      const hostPlayer = { username: user?.username, avatar: user?.avatar }
      // Guests get a real anonymous Firebase uid so the createParty write passes
      // the Firestore signedIn() rule; signed-in users keep their real uid.
      const uid = await ensureAnonymousAuth()
      if (!uid) { showToast('Could not start the party. Please try again.'); return }
      const { partyId: newPartyId, code } = await createParty(uid, mode, hostPlayer)
      setPartyId(newPartyId)
      setPartyCode(code)
      setPartyRole('host')
      setPartyMyUid(uid)
      const unsub = subscribeToParty(newPartyId, (data) => setPartyData(data))
      partyUnsubRef.current = unsub
      setGameState('party-lobby')
    } catch (err) {
      console.error('[Party] createParty error:', err)
      showToast('Error creating party: ' + err.message)
    }
  }

  const handleReset = (target = 'home') => {
    cleanupParty()
    // Remove the active saved game only if it's finished (completed or abandoned).
    // If status is 'playing', the entry stays in the dropdown so user can resume.
    const id = activeSavedGameIdRef.current
    if (id) {
      const entry = getSavedGameById(id)
      if (entry?.status === 'completed' || entry?.status === 'abandoned') {
        removeSavedGame(id)
      }
      activeSavedGameIdRef.current = null
      refreshSavedGames()
    }
    setSetupActors([null, null])
    setActiveGameType(null)
    setActiveTicketCost(0)
    setGameState(target)
    setStartActor(null)
    setEndActor(null)
    setChain([])
    setItems([])
    setView('movies')
    setSelectedMovie(null)
    setError(null)
    setMoveCount(0)
    setElapsed(0)
    setStartTime(null)
    setFinalStats(null)
    setPendingJumpIndex(null)
    setIsDailyChallenge(false)
    setIsHistoricalPuzzle(false)
    setDailyNumber(null)
    setCurrentDailyDate(null)
    setDailyCompletedData(null)
    setDailyStatus('loading')
    setReviewData(null)
    setStatsOrigin('home')
    setCueCardsUsed(0)
    setRevealedHints([])
    setCueCardDialogOpen(false)
    setCueCardResult(null)
    setCueCardLoading(false)
    setGoalActorMovies([])
    setHistoricalPrevBest(null)
    setIsNewHistoricalBest(false)
    setActiveChallenge(null)
    setH2hRole(null)
    setH2hPickStep(null)
    setH2hPendingStart(null)
    setH2hLobbyGameId(null)
    setH2hLobbyCode(null)
    setH2hLobbyRole(null)
    setH2hGetReadyData(null)
    setH2hPersonalResult(null)
    setH2hOpponentActor(null)
    setH2hResultsGameData(null)
    setH2hForfeitInfo(null)
    setH2hOpponentForfeited(false)
    forfeitHandledRef.current = false
  }

  // Restart the exact same puzzle from scratch (same start/end actors, same mode).
  const handlePlayAgain = async () => {
    const sa = startActor
    const ea = endActor
    const wasHistorical = isHistoricalPuzzle
    const prevNum = dailyNumber
    const prevDate = currentDailyDate
    const prevBest = historicalPrevBest

    // Clear game progress but keep mode context
    setChain([])
    setItems([])
    setView('movies')
    setSelectedMovie(null)
    setError(null)
    setMoveCount(0)
    setElapsed(0)
    setStartTime(null)
    setFinalStats(null)
    setPendingJumpIndex(null)
    setCueCardsUsed(0)
    setRevealedHints([])
    setCueCardResult(null)
    setCueCardLoading(false)
    setCueCardDialogOpen(false)
    setGoalActorMovies([])
    setIsNewHistoricalBest(false)

    // Preserve historical mode context for the replayed game
    if (wasHistorical) {
      setHistoricalPrevBest(getDailyCompletionForDate(prevDate) ?? prevBest)
    }

    await handleGameStart(sa, ea)
  }

  // Navigate to the current mode's setup/selection screen.
  const handleNewGame = () => {
    if (isHistoricalPuzzle) {
      handleReset('historical')
    } else {
      handleReset('single-player')
    }
  }

  const handleDailyChallenge = async () => {
    // Normal daily entry — back from the Daily screen returns to home.
    setDailyReturnTo('home')
    const today = getLocalDateStr()
    const puzzleNum = getPuzzleNumber(today)

    const completion = getDailyCompletionForDate(today)
    if (completion) {
      setDailyNumber(puzzleNum)
      setDailyCompletedData(completion)
      setCurrentDailyDate(today)
      setDailyStatus('completed')
      setGameState('daily')
      return
    }

    // Daily games are free so they aren't tracked in the new saved games system.
    setDailyStatus('loading')
    setGameState('daily')
    try {
      const { startActor, endActor } = await getOrGenerateTodaysPuzzle()
      setIsDailyChallenge(true)
      setDailyNumber(puzzleNum)
      setCurrentDailyDate(today)
      await handleGameStart(startActor, endActor)
    } catch {
      setDailyStatus('no-puzzle')
    }
  }

  const handleViewFilmStrip = () => {
    if (!dailyCompletedData || !dailyNumber) return
    setReviewData({ completedData: dailyCompletedData, dailyNumber })
    setStatsOrigin('daily')
    setGameState('film-review')
  }

  const handleViewStats = (_origin = 'home') => {
    setGameState('profile')
  }

  const handleExpand = useCallback((type, data, rect) => {
    setExpandedCard({ type, data, rect, isTarget: type === 'actor' && data.id === endActor?.id })
  }, [endActor])

  const handleOverlaySelect = (type, data, cast) => {
    setExpandedCard(null)
    if (type === 'movie') handleMovieSelect(data, cast)
    else handleActorSelect(data)
  }

  const canGoBack = chain.length > 1 && !loading

  const handleBack = async () => {
    if (!canGoBack) return
    const removedItem = chain[chain.length - 1]
    const newChain = chain.slice(0, -1)
    setChain(newChain)
    if (removedItem?.type === 'actor') setMoveCount((c) => Math.max(0, c - 1))
    window.scrollTo(0, 0)

    const lastItem = newChain[newChain.length - 1]

    if (lastItem.type === 'actor') {
      await loadActorMovies(lastItem.id)
    } else {
      setView('cast')
      setSelectedMovie(lastItem)
      setLoading(true)
      setError(null)
      try {
        const cast = await getMovieCast(lastItem.id)
        setItems(await injectGuaranteedActors(cast, lastItem.id))
      } catch {
        setError('Failed to load cast.')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleFilmstripClick = (index) => {
    if (index >= chain.length - 1 || loading) return
    setPendingJumpIndex(index)
  }

  const handleConfirmJump = async () => {
    const index = pendingJumpIndex
    setPendingJumpIndex(null)
    if (index == null) return

    const hopsRemoved = chain.slice(index + 1).filter(item => item.type === 'actor').length
    const newChain = chain.slice(0, index + 1)
    setChain(newChain)
    setMoveCount((c) => Math.max(0, c - hopsRemoved))
    window.scrollTo(0, 0)

    const item = newChain[newChain.length - 1]
    if (item.type === 'actor') {
      await loadActorMovies(item.id)
    } else {
      setView('cast')
      setSelectedMovie(item)
      setLoading(true)
      setError(null)
      try {
        const cast = await getMovieCast(item.id)
        setItems(await injectGuaranteedActors(cast, item.id))
      } catch {
        setError('Failed to load cast.')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleCancelJump = () => setPendingJumpIndex(null)

  const hopsAhead = pendingJumpIndex != null
    ? chain.slice(pendingJumpIndex + 1).filter(item => item.type === 'actor').length
    : 0

  // Cue Card available in all modes (single, daily, historical, H2H)
  const cueCardAvailable = goalActorMovies.length > 0

  const clearH2HState = () => {
    setActiveChallenge(null)
    setH2hRole(null)
    setH2hPickStep(null)
    setH2hPendingStart(null)
    setH2hGameCode(null)
    setH2hMyResult(null)
    setH2hSyncedChallenge(null)
  }

  // ── Quit ("Call Cut") handlers ────────────────────────────────────────────

  /**
   * Determines the game mode of the active gameplay and confirms quit.
   * Single / Daily / Historical → clear state, go home.
   * H2H → write abandoned to Firestore, clear state, go home.
   * Co-Op → handled inside CoopGameplay.jsx via the onAbandon prop.
   */
  const handleConfirmQuit = async () => {
    setQuitDialogOpen(false)

    // ── Party Battle Royale / Trilogy quit path ──
    if (activeGameType === 'party-battle' || activeGameType === 'party-trilogy') {
      if (partyId && partyMyUid) {
        updateParty(partyId, {
          [`players.${partyMyUid}.status`]: 'dnf',
        }).catch(() => {})
      }
      if (activeGameType === 'party-trilogy') {
        // Stay connected so player can see round standings & rejoin next round
        setActiveGameType(null)
        setGameState('party-trilogy-done')
      } else {
        cleanupParty()
        handleReset('home')
      }
      return
    }

    const isH2H = !!h2hRole && !!h2hLobbyGameId && gameState === 'playing'

    if (isH2H) {
      // ── H2H quit path ────────────────────────────────────────────────────
      // Use the persisted refundEligible flag (written to localStorage on every
      // hop) as the source of truth — more reliable than the moveCount closure.
      const h2hEntry = activeSavedGameIdRef.current
        ? getSavedGameById(activeSavedGameIdRef.current)
        : null
      // refundEligible: true  → no hops made yet (0-hop forfeit / game cancel)
      // refundEligible: false → at least 1 hop made (forfeit with progress)
      const forfeitedAt0Hops = h2hEntry ? (h2hEntry.refundEligible === true) : (moveCount === 0)
      console.log('[handleConfirmQuit] H2H hops at quit:', moveCount, '| refundEligible:', h2hEntry?.refundEligible, '| forfeitedAt0Hops:', forfeitedAt0Hops)
      if (forfeitedAt0Hops) {
        // 0 hops → quitter gets their ticket back
        const newCount = refundTicket(1)
        setTickets(newCount)
        showToast('Ticket refunded!')
      }
      try {
        await abandonGame(h2hLobbyGameId, h2hLobbyRole ?? h2hRole, forfeitedAt0Hops)
      } catch (err) {
        console.error('[App] abandonGame error:', err)
      }
      if (activeSavedGameIdRef.current) {
        updateSavedGame(activeSavedGameIdRef.current, { status: 'abandoned' })
      }
      handleReset('home')
      return
    }

    // ── Single / Daily / Historical quit path ────────────────────────────
    if (activeSavedGameIdRef.current) {
      const entry = getSavedGameById(activeSavedGameIdRef.current)
      if (entry?.refundEligible) {
        // Refund-eligible: refund ticket and abandon — entry removed by handleReset
        const newCount = refundTicket(entry.ticketSpent ?? 1)
        setTickets(newCount)
        showToast('Ticket refunded!')
        updateSavedGame(activeSavedGameIdRef.current, { status: 'abandoned' })
      } else {
        // No refund, single-player: pause and save for later
        updateSavedGame(activeSavedGameIdRef.current, {
          status: 'paused',
          chain,
          hops: moveCount,
          actors: { start: actorSnap(startActor), goal: actorSnap(endActor) },
        })
        showToast('Game saved!')
      }
    }
    handleReset('home')
  }

  // ── H2H abandon detection (opponent quit during our gameplay or while we wait) ──
  useEffect(() => {
    // Active during live gameplay AND while waiting on personal results screen
    const isH2HActive = (gameState === 'playing' || gameState === 'h2h-personal-results') &&
      !!h2hRole && !!h2hLobbyGameId
    if (!isH2HActive) return

    forfeitHandledRef.current = false // reset guard on each H2H session

    const unsub = subscribeToGame(h2hLobbyGameId, (data) => {
      if (
        data.status === 'abandoned' &&
        data.abandonedBy !== (h2hLobbyRole ?? h2hRole) &&
        !forfeitHandledRef.current
      ) {
        forfeitHandledRef.current = true
        // Opponent forfeited — we always get a refund
        const newCount = refundTicket(1)
        setTickets(newCount)
        if (activeSavedGameIdRef.current) {
          updateSavedGame(activeSavedGameIdRef.current, { status: 'abandoned' })
          refreshSavedGames()
        }
        const at0Hops = data.forfeitedAt0Hops ?? false
        setH2hForfeitInfo({ forfeitedAt0Hops: at0Hops })
        setH2hOpponentForfeited(true)
        // Do NOT navigate away — show popup overlay over current gameplay
      }
    })
    return () => unsub()
  }, [gameState, h2hLobbyGameId, h2hLobbyRole, h2hRole])

  const openTicketStore = useCallback(() => {
    if (!currentUser) {
      setSignInMessage('Create an account to purchase tickets.')
      setSignInOpen(true)
      return
    }
    setTicketStoreOpen(true)
  }, [currentUser])

  const navigateRaw = (id) => {
    setMenuOpen(false)
    if (id !== 'multiplayer') clearH2HState()
    if (id === 'home') handleReset('home')
    else if (id === 'play') handleSinglePlayerClick()
    else if (id === 'multiplayer') setGameState('multiplayer')
    else if (id === 'historical') setGameState('historical')
    else if (id === 'stats') setGameState('profile')
    else if (id === 'profile') setGameState('profile')
    else if (id === 'settings') setGameState('settings')
    else if (id === 'how-to-play') {
      howToPlayReturnRef.current = 'home'
      setGameState('how-to-play')
    }
    else if (id === 'store') openTicketStore()
  }

  const navigate = (id) => {
    setMenuOpen(false)
    if (id === 'store') { openTicketStore(); return }
    // Host on any party screen — confirm before navigating away
    if (isHostOnPartyScreen) {
      requestHostExit(() => navigateRaw(id))
      return
    }
    guardedNav(() => navigateRaw(id))
  }

  // Keep handleResetRef in sync with the latest closure so the host-exit
  // guard can always reach the current handleReset.
  useEffect(() => { handleResetRef.current = handleReset })

  // Browser back gesture: intercept for host on party screens
  useEffect(() => {
    if (!isHostOnPartyScreen) return
    window.history.pushState({ partyHostGuarded: true }, '')
    const onPop = () => {
      // Re-push so the back stays trapped until user confirms
      window.history.pushState({ partyHostGuarded: true }, '')
      requestHostExit(() => handleResetRef.current?.('home'))
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [isHostOnPartyScreen, requestHostExit])

  // Tab close / reload: show the browser's native "Leave site?" dialog
  useEffect(() => {
    if (!isHostOnPartyScreen) return
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = '' // Required for Chrome
      return ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isHostOnPartyScreen])

  // ── Firebase lobby handlers ───────────────────────────

  const handleStartH2H = async () => {
    if (!currentUser) {
      setSignInMessage('Create a free account to unlock all game modes and earn tickets.')
      setSignInOpen(true)
      return
    }
    if (tickets < 1) { setTicketStoreOpen(true); return }
    try {
      const { gameId, code } = await createGame('h2h')
      setTickets((t) => t - 1)
      setH2hLobbyGameId(gameId)
      setH2hLobbyCode(code)
      setH2hLobbyRole('p1')
      setH2hLobbyGameType('h2h')
      const entry = createSavedGame({ type: 'h2h', status: 'lobby', ticketSpent: 1, gameId, gameCode: code, playerKey: 'p1' })
      activeSavedGameIdRef.current = entry.id
      refreshSavedGames()
      setGameState('mp-lobby')
    } catch (err) {
      console.error('createGame error:', err)
      showToast('Error: ' + err.message)
    }
  }

  const handleStartCoop = async () => {
    if (!currentUser) {
      setSignInMessage('Create a free account to unlock all game modes and earn tickets.')
      setSignInOpen(true)
      return
    }
    if (tickets < 1) { setTicketStoreOpen(true); return }
    try {
      const { gameId, code } = await createGame('coop')
      setTickets((t) => t - 1)
      setH2hLobbyGameId(gameId)
      setH2hLobbyCode(code)
      setH2hLobbyRole('p1')
      setH2hLobbyGameType('coop')
      const entry = createSavedGame({ type: 'coop', status: 'lobby', ticketSpent: 1, gameId, gameCode: code, playerKey: 'p1' })
      activeSavedGameIdRef.current = entry.id
      refreshSavedGames()
      setGameState('mp-lobby')
    } catch (err) {
      console.error('createGame error:', err)
      showToast('Error: ' + err.message)
    }
  }

  const handleJoinLobby = async (code) => {
    try {
      // Auth FIRST — before ANY Firestore read or write. The /parties rules
      // require request.auth != null, so even the findPartyByCode READ below is
      // rejected for an unauthenticated guest. Sign in anonymously up front so
      // both the read and the write carry a valid token; signed-in users keep
      // their real uid.
      const uid = await ensureAnonymousAuth()
      if (!uid) return 'Could not join. Check your connection and try again.'

      // Check if it's a party code first — joining a party lobby is FREE.
      // The ticket is charged later when the player taps "Let's Go!" on the
      // pre-start confirmation screen.
      const partyDoc = await findPartyByCode(code)
      if (partyDoc) {
        if (partyDoc.status !== 'lobby') return 'This party has already started.'
        // If you're the host, switch to host view instead of joining as a player
        if (uid === partyDoc.hostId) {
          setPartyId(partyDoc.id)
          setPartyCode(partyDoc.code)
          setPartyRole('host')
          setPartyMyUid(uid)
          const unsub = subscribeToParty(partyDoc.id, (data) => setPartyData(data))
          partyUnsubRef.current = unsub
          setGameState('party-lobby')
          return null
        }
        const player = { uid, username: user?.username, avatar: user?.avatar }
        const result = await joinParty(code, player)
        if (result.error === 'not_found') return 'Party not found. Check your code.'
        if (result.error === 'already_started') return 'This party has already started.'
        setPartyId(result.partyId)
        setPartyCode(result.code)
        setPartyRole('player')
        setPartyMyUid(uid)
        const unsub = subscribeToParty(result.partyId, (data) => setPartyData(data))
        partyUnsubRef.current = unsub
        setGameState('party-player-lobby')
        return null
      }

      // Not a party — try as a game code (ticket required up front)
      if (!currentUser) {
        setSignInMessage('Create a free account to unlock all game modes and earn tickets.')
        setSignInOpen(true)
        return null
      }
      if (tickets < 1) { setTicketStoreOpen(true); return 'Not enough tickets.' }
      const result = await joinGame(code)
      if (result.error === 'not_found') return 'Game not found. Check your code.'
      if (result.error === 'already_started') return 'This game has already started.'
      setTickets((t) => t - 1)
      setH2hLobbyGameId(result.gameId)
      setH2hLobbyCode(result.code)
      setH2hLobbyRole('p2')
      const joinedType = result.gameType ?? 'h2h'
      setH2hLobbyGameType(joinedType)
      const entry = createSavedGame({ type: joinedType, status: 'lobby', ticketSpent: 1, gameId: result.gameId, gameCode: result.code, playerKey: 'p2' })
      activeSavedGameIdRef.current = entry.id
      refreshSavedGames()
      setGameState('mp-lobby')
      return null
    } catch {
      return 'Connection error. Check your internet and try again.'
    }
  }

  const handleAbandonLobby = async () => {
    // Always refund — lobby abandon is always 0 hops, always eligible
    const newCount = refundTicket(1)
    setTickets(newCount)
    showToast('Ticket refunded!')
    if (h2hLobbyGameId) {
      try { await deleteGame(h2hLobbyGameId) } catch {}
    }
    if (activeSavedGameIdRef.current) {
      removeSavedGame(activeSavedGameIdRef.current)
      activeSavedGameIdRef.current = null
      refreshSavedGames()
    }
    setH2hLobbyGameId(null)
    setH2hLobbyCode(null)
    setH2hLobbyRole(null)
    setGameState('multiplayer')
  }

  // Both P1 (opponent joined) and P2 (auto-advance) land here → actor pick
  const handleLobbyOpponentJoined = () => {
    if (activeSavedGameIdRef.current) {
      updateSavedGame(activeSavedGameIdRef.current, { status: 'picking' })
      refreshSavedGames()
    }
    setGameState('h2h-actor-pick')
  }

  const handleH2hActorPickAbandon = async () => {
    if (h2hLobbyGameId) {
      try { await deleteGame(h2hLobbyGameId) } catch {}
    }
    if (activeSavedGameIdRef.current) {
      removeSavedGame(activeSavedGameIdRef.current)
      activeSavedGameIdRef.current = null
      refreshSavedGames()
    }
    setH2hLobbyGameId(null)
    setH2hLobbyCode(null)
    setH2hLobbyRole(null)
    setH2hGetReadyData(null)
    setGameState('multiplayer')
  }

  // Called by H2HActorPicker when both players have locked different actors
  // and P1 has written startActor/goalActor to Firestore (status → 'playing')
  const handleH2hGameReady = ({ startActor, goalActor }) => {
    setH2hGetReadyData({ startActor, goalActor })
    if (activeSavedGameIdRef.current) {
      updateSavedGame(activeSavedGameIdRef.current, {
        actors: { start: actorSnap(startActor), goal: actorSnap(goalActor) },
      })
      refreshSavedGames()
    }
    setGameState('h2h-get-ready')
  }

  // Called when the 3-second get-ready countdown finishes
  const handleGetReadyComplete = async () => {
    if (!h2hGetReadyData) return
    if (h2hLobbyGameType === 'coop') {
      // Phase B: route to co-op gameplay
      if (activeSavedGameIdRef.current) {
        updateSavedGame(activeSavedGameIdRef.current, { status: 'playing' })
        refreshSavedGames()
      }
      setGameState('coop-gameplay')
      return
    }
    setH2hRole(h2hLobbyRole)
    setActiveGameType('multiplayer')
    setActiveTicketCost(0) // already deducted at lobby creation / join
    if (activeSavedGameIdRef.current) {
      updateSavedGame(activeSavedGameIdRef.current, { status: 'playing' })
      refreshSavedGames()
    }
    await handleGameStart(h2hGetReadyData.startActor, h2hGetReadyData.goalActor)
  }

  const handlePlayWhileWait = () => {
    setH2hRole('p1')
    setH2hPickStep('start')
    setGameState('h2h-pick')
  }

  const handleJoinChallenge = (challenge) => {
    if (tickets < 3) {
      setTicketStoreOpen(true)
      return
    }
    setTickets((t) => t - 3)
    const updated = updateChallenge(challenge.gameId, {
      player2: { username: user?.username, avatar: user?.avatar },
      status: 'in-progress',
    }) || challenge
    setActiveChallenge(updated)
    setH2hRole('p2')
    setH2hPickStep('start')
    setH2hPendingStart(null)
    setGameState('h2h-pick')
  }

  const handleResumeChallenge = async (challenge, role) => {
    setActiveChallenge(challenge)
    setH2hRole(role)
    const startSnap = role === 'p1' ? challenge.p1StartActor : challenge.p2StartActor
    const goalSnap = challenge.goalActor
    if (startSnap && goalSnap) {
      await handleGameStart(startSnap, goalSnap)
    } else {
      setH2hPickStep('start')
      setGameState('h2h-pick')
    }
  }

  const handleH2hActorPicked = async (actor) => {
    if (h2hRole === 'p1' && h2hPickStep === 'goal') {
      const gameId = generateGameCode()
      const challenge = {
        gameId,
        createdAt: new Date().toISOString(),
        status: 'waiting',
        player1: { username: user?.username, avatar: user?.avatar },
        player2: null,
        goalActor: actorSnapshot(actor),
        p1StartActor: null,
        p2StartActor: null,
        player1Result: null,
        player2Result: null,
      }
      saveChallenge(challenge)
      setActiveChallenge(challenge)
      setH2hGameCode(gameId)
      setGameState('h2h-code-reveal')
      return
    }
    if (h2hRole === 'p1' && h2hPickStep === 'start') {
      if (activeChallenge?.goalActor?.id === actor.id) return
      const startSnap = actorSnapshot(actor)
      const updated = updateChallenge(activeChallenge.gameId, { p1StartActor: startSnap })
        || { ...activeChallenge, p1StartActor: startSnap }
      setActiveChallenge(updated)
      setActiveGameType('multiplayer')
      setActiveTicketCost(3)
      await handleGameStart(startSnap, activeChallenge.goalActor)
      return
    }
    if (h2hRole === 'p2' && h2hPickStep === 'start') {
      if (activeChallenge?.goalActor?.id === actor.id) return
      const startSnap = actorSnapshot(actor)
      const updated = updateChallenge(activeChallenge.gameId, { p2StartActor: startSnap })
        || { ...activeChallenge, p2StartActor: startSnap }
      setActiveChallenge(updated)
      setActiveGameType('multiplayer')
      setActiveTicketCost(3)
      await handleGameStart(startSnap, activeChallenge.goalActor)
      return
    }
  }

  const handleH2hCancel = () => {
    setActiveChallenge(null)
    setH2hRole(null)
    setH2hPickStep(null)
    setH2hPendingStart(null)
    setGameState('multiplayer')
  }

  const renderBody = () => {
    // eslint-disable-next-line no-console
    console.log('[App] renderBody', { gameState, partyId, partyRole, hasPartyData: !!partyData, authReady, hasUser: !!currentUser })

    // ── Public URL routing — /terms, /privacy, /support ─────────────────────
    // These pages are accessible directly via URL (for App Store submission) and
    // render without auth. Checked before any gameState logic.
    const pathname = window.location.pathname
    if (pathname === '/terms')   return <TermsPage />
    if (pathname === '/privacy') return <PrivacyPage />
    if (pathname === '/support') return <SupportPage />

    if (gameState === 'home') {
      return (
        <HomeScreen
          savedGames={savedGames}
          streak={streak?.currentStreak ?? 0}
          onResume={handleResumeGame}
          onAbandon={handleAbandonSavedGame}
          onDailyChallenge={handleDailyChallenge}
          onViewFilmStrip={() => {
            const today = getLocalDateStr()
            const puzzleNum = getPuzzleNumber(today)
            const completion = getDailyCompletionForDate(today)
            if (!completion) return
            setDailyCompletedData(completion)
            setDailyNumber(puzzleNum)
            setCurrentDailyDate(today)
            setDailyStatus('completed')
            setReviewData({ completedData: completion, dailyNumber: puzzleNum })
            setStatsOrigin('daily')
            setGameState('film-review')
          }}
          onTerms={() => { legalReturnRef.current = 'home'; setGameState('terms') }}
          onPrivacy={() => { legalReturnRef.current = 'home'; setGameState('privacy') }}
          onSupport={() => { legalReturnRef.current = 'home'; setGameState('support') }}
          isGuest={!currentUser}
          onSignUp={() => { setSignInMessage('Sign up to claim 10 free tickets and keep your streak & stats.'); setSignInOpen(true) }}
        />
      )
    }

    // The legal pages now render a single Home circle via BottomNav, so back
    // always goes home regardless of entry context (was previously routed via
    // legalReturnRef). The ref still gets set by Settings/SignIn for any other
    // consumers but is no longer read here.
    if (gameState === 'terms')   return <TermsPage   onBack={() => setGameState('home')} />
    if (gameState === 'privacy') return <PrivacyPage onBack={() => setGameState('home')} />
    if (gameState === 'support') return <SupportPage onBack={() => setGameState('home')} />

    if (gameState === 'settings') {
      return (
        <SettingsScreen
          settings={settings}
          onUpdateSettings={updateSettings}
          onHowToPlay={() => {
            howToPlayReturnRef.current = 'settings'
            setGameState('how-to-play')
          }}
          onTerms={() => { legalReturnRef.current = 'settings'; setGameState('terms') }}
          onPrivacy={() => { legalReturnRef.current = 'settings'; setGameState('privacy') }}
          onSupport={() => { legalReturnRef.current = 'settings'; setGameState('support') }}
          onBack={() => setGameState('home')}
        />
      )
    }

    if (gameState === 'profile') {
      return (
        <ProfileScreen
          key={statsKey}
          user={user}
          currentUser={currentUser}
          activeTheme={activeTheme}
          streak={streak}
          tickets={displayTickets}
          todayDate={currentDailyDate || getLocalDateStr()}
          unlockedThemes={unlockedThemes}
          onSetTheme={handleSetTheme}
          onPurchaseTheme={handlePurchaseTheme}
          onUpdateUser={updateUser}
          onSignIn={() => setSignInOpen(true)}
          onSignOut={handleSignOut}
          onDeleteAccount={handleDeleteAccount}
          onBack={() => setGameState('home')}
        />
      )
    }

    if (gameState === 'how-to-play') {
      return (
        <HowToPlay
          onBack={() => setGameState(howToPlayReturnRef.current || 'home')}
        />
      )
    }

    if (gameState === 'historical') {
      return (
        <HistoricalPuzzles
          tickets={displayTickets}
          onPlay={handleHistoricalPlay}
          onOpenStore={openTicketStore}
          onBack={() => setGameState('single-player')}
        />
      )
    }

    if (gameState === 'daily') {
      return (
        <DailyChallenge
          status={dailyStatus}
          dailyNumber={dailyNumber}
          dailyDate={currentDailyDate}
          completedData={dailyCompletedData}
          onHome={() => setGameState('home')}
          onViewStats={() => handleViewStats('daily')}
        />
      )
    }

    if (gameState === 'film-review') {
      return (
        <FilmStripReview
          dailyNumber={reviewData?.dailyNumber}
          completedData={reviewData?.completedData}
          onViewStats={() => handleViewStats('film-review')}
          onBack={() => setGameState('daily')}
          onHome={() => setGameState('home')}
        />
      )
    }

    // gameState === 'stats' is retired — stats live inside ProfileScreen

    if (gameState === 'setup') {
      // Refund-and-abandon flow shared by the bottom-nav back/home circles.
      // The "Single Player" circle goes to single-player; "Home" goes to home.
      // In both cases we refund any ticket spent on the saved-game slot.
      const abandonAndGoTo = (dest) => {
        if (activeSavedGameIdRef.current) {
          const entry = getSavedGameById(activeSavedGameIdRef.current)
          if (entry?.refundEligible !== false) {
            const newCount = refundTicket(entry?.ticketSpent ?? 1)
            setTickets(newCount)
            updateSavedGame(activeSavedGameIdRef.current, { status: 'abandoned' })
          }
        }
        handleReset(dest)
      }
      return (
        <SetupScreen
          onStart={handleGameStart}
          onBack={() => abandonAndGoTo('single-player')}
          onHome={() => abandonAndGoTo('home')}
          initialActors={setupActors}
          onActorsChange={setSetupActors}
        />
      )
    }

    if (gameState === 'single-player') {
      return (
        <SinglePlayer
          tickets={displayTickets}
          onDailyChallenge={handleDailyChallenge}
          dailyCompleted={!!getDailyCompletionForDate(getLocalDateStr())}
          onViewFilmStrip={() => {
            // "✅ Completed Today" tap → open the Daily Completed screen
            // ("You've already wrapped today's shoot!" with the filmstrip).
            // dailyReturnTo='single-player' so its back button returns here.
            const today = getLocalDateStr()
            const completion = getDailyCompletionForDate(today)
            if (!completion) return
            const puzzleNum = getPuzzleNumber(today)
            setDailyCompletedData(completion)
            setDailyNumber(puzzleNum)
            setCurrentDailyDate(today)
            setDailyStatus('completed')
            setDailyReturnTo('single-player')
            setGameState('daily')
          }}
          onStart={handleConfirmSinglePlayer}
          onOpenStore={openTicketStore}
          onHistoricalPuzzles={() => setGameState('historical')}
          onBack={() => setGameState('home')}
        />
      )
    }

    if (gameState === 'multiplayer') {
      return (
        <Multiplayer
          onStartH2H={handleStartH2H}
          onStartCoop={handleStartCoop}
          onStartParty={handleStartParty}
          onJoinLobby={handleJoinLobby}
          onBack={() => setGameState('home')}
          tickets={displayTickets}
          onOpenStore={openTicketStore}
        />
      )
    }

    if (gameState === 'party-mode-select') {
      // Party Mode is a real host popup (Multiplayer.jsx opens this URL with
      // ?host=1 via window.open). Close calls window.close() when we're in
      // that popup context, and falls back to the launcher when we're not
      // (popup-blocked path, where the host UI runs in the same window).
      return (
        <PartyModeSelect
          tickets={displayTickets}
          onSelectMode={handlePartyModeSelected}
          onClose={() => {
            if (isHostPopup) {
              try { window.close() } catch {}
            } else {
              setGameState('multiplayer')
            }
          }}
        />
      )
    }

    // Host re-selecting mode for an existing party (Play Again flow)
    if (gameState === 'party-mode-reselect') {
      return (
        <PartyModeSelect
          tickets={displayTickets}
          onSelectMode={async (mode) => {
            if (!partyId) return
            await updateParty(partyId, { mode })
            setGameState('party-lobby')
          }}
          onClose={async () => {
            // Reselect path: end the party first (cleanup matches the prior
            // onBack handler), then close the popup window (or fall back to
            // routing to Multiplayer when not in popup context).
            if (partyId) {
              await updateParty(partyId, { status: 'ended' }).catch(() => {})
            }
            cleanupParty()
            if (isHostPopup) {
              try { window.close() } catch {}
            } else {
              setGameState('multiplayer')
            }
          }}
        />
      )
    }

    if (gameState === 'party-lobby') {
      return (
        <PartyLobby
          party={partyData}
          currentUserId={partyMyUid}
          onEveryoneIn={async () => {
            if (!partyId) return
            // Transition to pre-start so each player sees a ticket confirmation prompt
            const playerUids = Object.keys(partyData?.players ?? {})
            await initiateStart(partyId, playerUids)
          }}
          onForceStart={async () => {
            if (!partyId) return
            const mode = partyData?.mode
            if (mode === 'co-op') {
              // Co-Op: Reaction Test FIRST (determines turn order),
              // then actor pickers are randomly assigned by the reaction-test finalizer.
              await updateParty(partyId, {
                status: 'reaction_test',
                reactionTaps: {},
                clapperboardAt: null,
              })
              setGameState('party-reaction-test')
            } else if (mode === 'directors-cut') {
              // Director's Cut: host picks the Director from a tappable grid.
              await updateParty(partyId, {
                status: 'director_selection',
                directorUid: null,
                dcStepStartedAt: new Date(),
              })
              setGameState('party-dc-director-select')
            } else if (mode === 'battle-royale' || mode === 'speed-run') {
              // Battle Royale / Speed Run: skip director — straight to actor picking
              await assignActorPickers(partyId)
              setGameState('party-actor-pick')
            } else if (mode === 'trilogy') {
              // Trilogy: initialize trilogy-specific fields then start round 1 actor picking
              await updateParty(partyId, {
                currentRound: 1,
                rounds: {
                  1: { startActor: null, goalActor: null, actorPickerIds: [], results: {} },
                  2: { startActor: null, goalActor: null, actorPickerIds: [], results: {} },
                  3: { startActor: null, goalActor: null, actorPickerIds: [], results: {} },
                },
                cumulativeScores: {},
              })
              await assignActorPickers(partyId)
              setGameState('party-actor-pick')
            } else {
              await assignDirector(partyId)
              setGameState('party-director-reveal')
            }
          }}
          onAbandon={async () => {
            // Host cancelled — mark party so all charged players refund themselves.
            // Delay deletion of the party doc so players have time to read the signal.
            if (partyId) {
              try {
                await endPartyFromLobby(partyId)
                const idToDelete = partyId
                setTimeout(() => { deleteParty(idToDelete).catch(() => {}) }, 8000)
              } catch {}
            }
            cleanupParty()
            setGameState('multiplayer')
            if (isHostPopup) { try { window.close() } catch {} }
          }}
        />
      )
    }

    if (gameState === 'party-player-lobby') {
      return (
        <PartyPlayerScreen
          party={partyData}
          currentUserId={partyMyUid || ''}
          currentUser={currentUser}
          onConfirmReady={async () => {
            if (!partyId || !partyMyUid) return
            // Double-charge guard: ref (same session) + Firestore array (cross-session / re-render)
            if (partyTicketChargedRef.current) return
            if ((partyData?.chargedPlayers ?? []).includes(partyMyUid)) return
            if (tickets < 1) { setTicketStoreOpen(true); return }
            partyTicketChargedRef.current = true
            setTickets(t => Math.max(0, t - 1))
            await confirmPlayerStart(partyId, partyMyUid)
          }}
          onLeave={async () => {
            // Joining was free — no refund needed unless they already confirmed (chargedPlayers).
            // leaveParty checks chargedPlayers internally and returns { refunded } accordingly.
            if (partyId && partyMyUid) {
              try {
                const { refunded } = await leaveParty(partyId, partyMyUid)
                if (refunded) {
                  const newCount = refundTicket(1)
                  setTickets(newCount)
                  showToast('Ticket refunded!')
                }
              } catch {}
            }
            cleanupParty()
            setGameState('multiplayer')
          }}
          onGameStart={(startAct, goalAct) => {
            setActiveGameType(partyData?.mode === 'trilogy' ? 'party-trilogy' : 'party-battle')
            handleGameStart(startAct, goalAct)
          }}
          onRefundMyTicket={(msg) => {
            const newCount = refundTicket(1)
            setTickets(newCount)
            showToast(msg || 'Ticket refunded!')
          }}
          onBackToLobby={() => {
            // A finished Trilogy (final_standings) is terminal — exit to home and
            // drop the party link (cleanupParty unsubscribes + clears partyId) so
            // session recovery can't re-attach. Between rounds, reset local state
            // and wait in the player lobby for the host to start the next round.
            if (partyData?.status === 'final_standings') {
              cleanupParty()
              setGameState('home')
              return
            }
            partyTicketChargedRef.current = false
            setChain([])
            setItems([])
            setView('movies')
            setStartActor(null)
            setEndActor(null)
            setMoveCount(0)
            setElapsed(0)
            setFinalStats(null)
            setActiveGameType(null)
            setGameState('party-player-lobby')
          }}
        />
      )
    }

    if (gameState === 'party-director-reveal') {
      return (
        <PartyDirectorReveal
          party={partyData}
          onContinue={async () => {
            // Director transitioned status either to 'team_setup' (most modes)
            // or 'actor_pick' (battle-royale skips team setup)
            if (partyData?.status === 'actor_pick') {
              setGameState('party-actor-pick')
            } else {
              setGameState('party-team-setup')
            }
          }}
        />
      )
    }

    if (gameState === 'party-team-setup') {
      // New tap-to-place team builder (read-only mirror for the host).
      // For H2H, the host auto-advance effect handles team_locked → actor_pick.
      // For other team-based modes (legacy), keep the old PartyTeamSetup as a
      // fallback when teams is an OBJECT (old shape) rather than an ARRAY.
      const teamsIsArray = Array.isArray(partyData?.teams)
      if (teamsIsArray || partyData?.mode === 'h2h') {
        return (
          <PartyTeamBuilder
            party={partyData}
            currentUserId={partyMyUid}
            role="host"
          />
        )
      }
      return (
        <PartyTeamSetup
          party={partyData}
          onTeamsLocked={async () => {
            if (!partyId) return
            await assignActorPickers(partyId)
            setGameState('party-actor-pick')
          }}
        />
      )
    }

    if (gameState === 'party-actor-pick') {
      return (
        <PartyActorPick
          party={partyData}
          onCountdownStart={async () => {
            if (!partyId) return
            // Instructions phase slots between cinematic reveal and LCA.
            // Host renders PartyInstructions; player phones see "watch the host".
            // PartyInstructions writes status: 'countdown' (via onFireLCA) when
            // the host taps "Action!" or "Skip" — and the host goes back to
            // 'party-actor-pick' so PartyActorPick can run the existing LCA logic.
            await updateParty(partyId, { status: 'instructions' })
            setGameState('party-instructions')
          }}
          onCountdownDone={async () => {
            if (!partyId) return
            const mode = partyData?.mode
            if (mode === 'co-op') {
              // Co-Op flow: pre-start → reaction_test → actor_pick → reveal → LCA → co-op-playing
              // Reaction test already happened (before actor pick) and seeded turnOrder.
              // Now LCA has fired — start the turn-based gameplay clock.
              await updateParty(partyId, {
                status: 'co-op-playing',
                playingStartedAt: new Date(),
                turnStartedAt: new Date(),
              })
              setGameState('party-coop-playing')
              return
            }
            if (mode === 'directors-cut') {
              // Director's Cut: after LCA, start the gameplay nomination loop
              await updateParty(partyId, {
                status: 'dc-playing',
                dcStep: 'movie-nominate',
                dcNominations: {},
                playingStartedAt: new Date(),
                dcStepStartedAt: new Date(),
              })
              setGameState('party-dc-playing')
              return
            }
            if (mode === 'h2h') {
              // H2H flow: pre-start → director → team_setup → team_locked → actor_pick → reveal → LCA → h2h-playing
              // Initialize per-team chain + turn indices.
              const teams = Array.isArray(partyData?.teams) ? partyData.teams : []
              const h2hChains = {}
              const teamTurnIndices = {}
              for (const t of teams) {
                h2hChains[t.id] = []
                teamTurnIndices[t.id] = 0
              }
              await updateParty(partyId, {
                status: 'h2h-playing',
                playingStartedAt: new Date(),
                h2hTurnStartedAt: new Date(),
                h2hChains,
                teamTurnIndices,
                currentTeamIndex: 0,
                h2hWinner: null,
              })
              setGameState('party-h2h-playing')
              return
            }
            if (mode === 'trilogy') {
              // Trilogy: LCA done — start round 1 gameplay
              await updateParty(partyId, {
                status: 'trilogy-playing',
                playingStartedAt: new Date(),
              })
              setGameState('party-trilogy-playing')
              return
            }
            // BR / Speed Run / other simultaneous modes: initialize gameplay directly
            const initUpdates = { status: 'playing', playingStartedAt: new Date() }
            if (partyData?.players) {
              for (const uid of Object.keys(partyData.players)) {
                initUpdates[`players.${uid}.status`] = 'playing'
                initUpdates[`players.${uid}.hops`] = 0
              }
            }
            await updateParty(partyId, initUpdates)
            setGameState('party-playing')
          }}
        />
      )
    }

    if (gameState === 'party-reaction-test') {
      return (
        <PartyReactionTest
          party={partyData}
          currentUserId={partyMyUid}
          forHost
        />
      )
    }

    if (gameState === 'party-coop-playing') {
      return (
        <PartyCoopGameplay
          party={partyData}
          currentUserId={partyMyUid}
          role="host"
        />
      )
    }

    if (gameState === 'party-coop-results') {
      return (
        <PartyCoopDone
          party={partyData}
          currentUserId={partyMyUid}
          forHost
          onPlayAgain={async () => {
            if (!partyId || !partyData) return
            const resetUpdates = {
              status: 'lobby',
              mode: null,
              directorId: null,
              startActor: null,
              goalActor: null,
              pick0: null,
              pick1: null,
              actorPickerIds: [],
              playingStartedAt: null,
              chargedPlayers: [],
              reactionTaps: {},
              clapperboardAt: null,
              turnOrder: [],
              currentTurnIndex: 0,
              coopChain: [],
              playerMoves: {},
              turnStartedAt: null,
              cameoLastBy: null,
              passedUids: [],
              finishedAt: null,
            }
            for (const uid of Object.keys(partyData.players ?? {})) {
              resetUpdates[`players.${uid}.status`] = null
              resetUpdates[`players.${uid}.hops`] = null
              resetUpdates[`players.${uid}.time`] = null
              resetUpdates[`players.${uid}.chain`] = null
              resetUpdates[`players.${uid}.finishedAt`] = null
              // Critical: reset pre-start confirmation flags so the next round's
              // pre-start phase starts from a clean 0/N confirmed count.
              resetUpdates[`players.${uid}.ready`] = false
              resetUpdates[`players.${uid}.lastSeenResults`] = null
            }
            await updateParty(partyId, resetUpdates)
            setGameState('party-mode-reselect')
          }}
          onBackToLobby={async () => {
            if (!partyId || !partyData) return
            const resetUpdates = {
              status: 'lobby',
              directorId: null,
              startActor: null,
              goalActor: null,
              pick0: null,
              pick1: null,
              actorPickerIds: [],
              playingStartedAt: null,
              chargedPlayers: [],
              reactionTaps: {},
              clapperboardAt: null,
              turnOrder: [],
              currentTurnIndex: 0,
              coopChain: [],
              playerMoves: {},
              turnStartedAt: null,
              cameoLastBy: null,
              passedUids: [],
              finishedAt: null,
            }
            for (const uid of Object.keys(partyData.players ?? {})) {
              resetUpdates[`players.${uid}.status`] = null
              resetUpdates[`players.${uid}.hops`] = null
              resetUpdates[`players.${uid}.time`] = null
              resetUpdates[`players.${uid}.chain`] = null
              resetUpdates[`players.${uid}.finishedAt`] = null
              resetUpdates[`players.${uid}.ready`] = false
              resetUpdates[`players.${uid}.lastSeenResults`] = null
            }
            await updateParty(partyId, resetUpdates)
            setGameState('party-lobby')
          }}
          onEndParty={async () => {
            if (!partyId) return
            await updateParty(partyId, { status: 'ended' }).catch(() => {})
            cleanupParty()
            setGameState('multiplayer')
            if (isHostPopup) { try { window.close() } catch {} }
          }}
        />
      )
    }

    if (gameState === 'party-instructions') {
      return (
        <PartyInstructions
          party={partyData}
          role="host"
          onFireLCA={async () => {
            if (!partyId) return
            // Open a fresh readiness barrier: clear stale acks as the countdown
            // starts, so the host waits for THIS round's loads. Players re-ack once
            // they observe status='countdown' and finish fetching the round data.
            const updates = { status: 'countdown' }
            for (const uid of Object.keys(partyData?.players ?? {})) {
              updates[`players.${uid}.lcaReady`] = false
            }
            await updateParty(partyId, updates)
            // Route host back to the actor-pick gameState so PartyActorPick's
            // existing LCA logic fires when status='countdown' is observed.
            setGameState('party-actor-pick')
          }}
        />
      )
    }

    if (gameState === 'party-h2h-playing') {
      return (
        <PartyH2HGameplay
          party={partyData}
          currentUserId={partyMyUid}
          role="host"
        />
      )
    }

    if (gameState === 'party-dc-director-select') {
      return (
        <PartyDCDirectorSelect
          party={partyData}
          currentUserId={partyMyUid}
          role="host"
        />
      )
    }

    if (gameState === 'party-dc-casting') {
      return (
        <PartyDCCasting
          party={partyData}
          currentUserId={partyMyUid}
          role="host"
        />
      )
    }

    if (gameState === 'party-dc-playing') {
      return (
        <PartyDCGameplay
          party={partyData}
          currentUserId={partyMyUid}
          role="host"
        />
      )
    }

    if (gameState === 'party-dc-results') {
      const dcReset = (target) => async () => {
        if (!partyId || !partyData) return
        const resetUpdates = {
          status: 'lobby',
          ...(target === 'mode-select' ? { mode: null } : {}),
          directorId: null,
          directorUid: null,
          startActor: null,
          goalActor: null,
          pick0: null,
          pick1: null,
          actorPickerIds: [],
          playingStartedAt: null,
          chargedPlayers: [],
          reactionTaps: {},
          clapperboardAt: null,
          turnOrder: [],
          currentTurnIndex: 0,
          coopChain: [],
          playerMoves: {},
          turnStartedAt: null,
          cameoLastBy: null,
          passedUids: [],
          finishedAt: null,
          // H2H reset
          teams: [],
          h2hChains: {},
          teamTurnIndices: {},
          currentTeamIndex: 0,
          h2hWinner: null,
          h2hTurnStartedAt: null,
          // DC reset
          dcChain: [],
          dcNominations: {},
          dcRoundHistory: [],
          agentPoints: {},
          dcWinner: null,
          castingNominations: {},
          startNominatorUid: null,
          goalNominatorUid: null,
          dcStep: null,
          dcLastPickedLabel: null,
          dcLastScoredUids: [],
          dcStepStartedAt: null,
          solvedAt: null,
        }
        for (const uid of Object.keys(partyData.players ?? {})) {
          resetUpdates[`players.${uid}.status`] = null
          resetUpdates[`players.${uid}.hops`] = null
          resetUpdates[`players.${uid}.time`] = null
          resetUpdates[`players.${uid}.chain`] = null
          resetUpdates[`players.${uid}.finishedAt`] = null
          resetUpdates[`players.${uid}.ready`] = false
          resetUpdates[`players.${uid}.lastSeenResults`] = null
          resetUpdates[`players.${uid}.teamId`] = null
        }
        await updateParty(partyId, resetUpdates)
        setGameState(target === 'mode-select' ? 'party-mode-reselect' : 'party-lobby')
      }
      return (
        <PartyDCDone
          party={partyData}
          currentUserId={partyMyUid}
          forHost
          onPlayAgain={dcReset('mode-select')}
          onBackToLobby={dcReset('lobby')}
          onEndParty={async () => {
            if (!partyId) return
            await updateParty(partyId, { status: 'ended' }).catch(() => {})
            cleanupParty()
            setGameState('multiplayer')
            if (isHostPopup) { try { window.close() } catch {} }
          }}
        />
      )
    }

    if (gameState === 'party-h2h-results') {
      const h2hReset = (target) => async () => {
        if (!partyId || !partyData) return
        const resetUpdates = {
          status: 'lobby',
          ...(target === 'mode-select' ? { mode: null } : {}),
          directorId: null,
          startActor: null,
          goalActor: null,
          pick0: null,
          pick1: null,
          actorPickerIds: [],
          playingStartedAt: null,
          chargedPlayers: [],
          reactionTaps: {},
          clapperboardAt: null,
          turnOrder: [],
          currentTurnIndex: 0,
          coopChain: [],
          playerMoves: {},
          turnStartedAt: null,
          cameoLastBy: null,
          passedUids: [],
          finishedAt: null,
          // H2H-specific reset
          teams: [],
          h2hChains: {},
          teamTurnIndices: {},
          currentTeamIndex: 0,
          h2hWinner: null,
          h2hTurnStartedAt: null,
          solvedAt: null,
        }
        for (const uid of Object.keys(partyData.players ?? {})) {
          resetUpdates[`players.${uid}.status`] = null
          resetUpdates[`players.${uid}.hops`] = null
          resetUpdates[`players.${uid}.time`] = null
          resetUpdates[`players.${uid}.chain`] = null
          resetUpdates[`players.${uid}.finishedAt`] = null
          resetUpdates[`players.${uid}.ready`] = false
          resetUpdates[`players.${uid}.lastSeenResults`] = null
          resetUpdates[`players.${uid}.teamId`] = null
        }
        await updateParty(partyId, resetUpdates)
        setGameState(target === 'mode-select' ? 'party-mode-reselect' : 'party-lobby')
      }
      return (
        <PartyH2HDone
          party={partyData}
          currentUserId={partyMyUid}
          forHost
          onPlayAgain={h2hReset('mode-select')}
          onBackToLobby={h2hReset('lobby')}
          onEndParty={async () => {
            if (!partyId) return
            await updateParty(partyId, { status: 'ended' }).catch(() => {})
            cleanupParty()
            setGameState('multiplayer')
            if (isHostPopup) { try { window.close() } catch {} }
          }}
        />
      )
    }

    if (gameState === 'party-playing') {
      return (
        <PartyBattleScreen
          party={partyData}
          onPlayAgain={async () => {
            if (!partyId || !partyData) return
            // Reset all players and party state — return everyone to lobby
            const resetUpdates = {
              status: 'lobby',
              mode: null,
              directorId: null,
              startActor: null,
              goalActor: null,
              pick0: null,
              pick1: null,
              actorPickerIds: [],
              playingStartedAt: null,
              chargedPlayers: [],
            }
            for (const uid of Object.keys(partyData.players ?? {})) {
              resetUpdates[`players.${uid}.status`] = null
              resetUpdates[`players.${uid}.hops`] = null
              resetUpdates[`players.${uid}.time`] = null
              resetUpdates[`players.${uid}.chain`] = null
              resetUpdates[`players.${uid}.finishedAt`] = null
            }
            await updateParty(partyId, resetUpdates)
            // Send host to mode select — players return to lobby via listener
            setGameState('party-mode-reselect')
          }}
          onBackToLobby={async () => {
            if (!partyId || !partyData) return
            // Reset game data, keep mode and players — everyone returns to lobby
            const resetUpdates = {
              status: 'lobby',
              directorId: null,
              startActor: null,
              goalActor: null,
              pick0: null,
              pick1: null,
              actorPickerIds: [],
              playingStartedAt: null,
              chargedPlayers: [],
            }
            for (const uid of Object.keys(partyData.players ?? {})) {
              resetUpdates[`players.${uid}.status`] = null
              resetUpdates[`players.${uid}.hops`] = null
              resetUpdates[`players.${uid}.time`] = null
              resetUpdates[`players.${uid}.chain`] = null
              resetUpdates[`players.${uid}.finishedAt`] = null
            }
            await updateParty(partyId, resetUpdates)
            // Host returns to lobby — players follow via their Firestore listener
            setGameState('party-lobby')
          }}
          onEndParty={async () => {
            if (!partyId) return
            await updateParty(partyId, { status: 'ended' }).catch(() => {})
            cleanupParty()
            setGameState('multiplayer')
            if (isHostPopup) { try { window.close() } catch {} }
          }}
        />
      )
    }

    // ── Trilogy host screen ────────────────────────────────────────────────────
    if (gameState === 'party-trilogy-playing') {
      return (
        <PartyTrilogyGameplay
          party={partyData}
          onNextRound={async () => {
            if (!partyId || !partyData) return
            const nextRound = (partyData.currentRound ?? 1) + 1
            // Pick the next round's blind actor-pickers up front so the whole
            // advance — currentRound bump, per-player state clear, AND
            // status:'actor_pick' — lands in ONE write. A two-step write (reset,
            // then assignActorPickers) left currentRound on the new round while
            // status was still 'round_standings' with an empty results map, which
            // other clients flashed as an all-DNF standings.
            const readyIds = Object.keys(partyData.players ?? {})
              .filter((uid) => partyData.players[uid]?.ready === true)
            const actorPickerIds = [...readyIds]
              .sort(() => Math.random() - 0.5)
              .slice(0, Math.min(2, readyIds.length))
            const resetUpdates = {
              currentRound:    nextRound,
              startActor:      null,
              goalActor:       null,
              pick0:           null,
              pick1:           null,
              actorPickerIds,
              playingStartedAt: null,
              status:          'actor_pick',
            }
            for (const uid of Object.keys(partyData.players ?? {})) {
              resetUpdates[`players.${uid}.status`]    = null
              resetUpdates[`players.${uid}.hops`]      = null
              resetUpdates[`players.${uid}.time`]      = null
              resetUpdates[`players.${uid}.chain`]     = null
              resetUpdates[`players.${uid}.finishedAt`] = null
              resetUpdates[`players.${uid}.lcaReady`]  = false
            }
            await updateParty(partyId, resetUpdates)
            setGameState('party-actor-pick')
          }}
          onPlayAgain={async () => {
            if (!partyId || !partyData) return
            // Full reset — clear all Trilogy-specific state and player data
            const resetUpdates = {
              status:           'lobby',
              mode:             null,
              startActor:       null,
              goalActor:        null,
              pick0:            null,
              pick1:            null,
              actorPickerIds:   [],
              playingStartedAt: null,
              chargedPlayers:   [],
              directorId:       null,
              currentRound:     null,
              rounds:           null,
              cumulativeScores: null,
            }
            for (const uid of Object.keys(partyData.players ?? {})) {
              resetUpdates[`players.${uid}.status`]     = null
              resetUpdates[`players.${uid}.hops`]       = null
              resetUpdates[`players.${uid}.time`]       = null
              resetUpdates[`players.${uid}.chain`]      = null
              resetUpdates[`players.${uid}.finishedAt`] = null
            }
            await updateParty(partyId, resetUpdates)
            setGameState('party-mode-reselect')
          }}
          onEndParty={async () => {
            if (!partyId) return
            await updateParty(partyId, { status: 'ended' }).catch(() => {})
            cleanupParty()
            setGameState('multiplayer')
            if (isHostPopup) { try { window.close() } catch {} }
          }}
        />
      )
    }

    // ── Trilogy player waiting/results screen ──────────────────────────────────
    // Player lands here after finishing a round (or quitting mid-round).
    // PartyPlayerScreen handles round_standings and final_standings displays.
    // The useEffect above auto-routes back to party-player-lobby when actor_pick / lobby begins.
    if (gameState === 'party-trilogy-done') {
      return (
        <PartyPlayerScreen
          party={partyData}
          currentUserId={partyMyUid || ''}
          onLeave={async () => {
            if (partyId && partyMyUid) {
              try {
                const { refunded } = await leaveParty(partyId, partyMyUid)
                if (refunded) {
                  const newCount = refundTicket(1)
                  setTickets(newCount)
                  showToast('Ticket refunded!')
                }
              } catch {}
            }
            cleanupParty()
            handleReset('home')
          }}
          onGameStart={(startAct, goalAct) => {
            // If player somehow ends up in trilogy-done while status is trilogy-playing,
            // launch the game and mark them as trilogy type.
            setActiveGameType('party-trilogy')
            handleGameStart(startAct, goalAct)
          }}
          onRefundMyTicket={(msg) => {
            const newCount = refundTicket(1)
            setTickets(newCount)
            showToast(msg || 'Ticket refunded!')
          }}
        />
      )
    }

    if (gameState === 'party-battle-done') {
      return (
        <PartyBattleDone
          party={partyData}
          currentUserId={partyMyUid}
          chain={chain}
          finalStats={finalStats}
          onLeave={() => {
            cleanupParty()
            handleReset('home')
          }}
          onBackToLobby={() => {
            // Reset game state for next round — keep party connection alive
            partyTicketChargedRef.current = false
            setChain([])
            setItems([])
            setView('movies')
            setStartActor(null)
            setEndActor(null)
            setMoveCount(0)
            setElapsed(0)
            setFinalStats(null)
            setActiveGameType(null)
            setGameState('party-player-lobby')
          }}
        />
      )
    }

    if (gameState === 'mp-lobby') {
      return (
        <MultiplayerLobby
          gameId={h2hLobbyGameId}
          code={h2hLobbyCode}
          role={h2hLobbyRole}
          gameType={h2hLobbyGameType}
          onOpponentJoined={handleLobbyOpponentJoined}
          onAbandon={handleAbandonLobby}
        />
      )
    }

    if (gameState === 'h2h-actor-pick') {
      return (
        <H2HActorPicker
          gameId={h2hLobbyGameId}
          playerRole={h2hLobbyRole}
          gameType={h2hLobbyGameType}
          onGameReady={handleH2hGameReady}
          onAbandon={handleH2hActorPickAbandon}
        />
      )
    }

    if (gameState === 'h2h-get-ready') {
      return (
        <H2HGetReady
          startActor={h2hGetReadyData?.startActor}
          goalActor={h2hGetReadyData?.goalActor}
          onStart={handleGetReadyComplete}
        />
      )
    }

    if (gameState === 'coop-gameplay') {
      return (
        <CoopGameplay
          gameId={h2hLobbyGameId}
          playerKey={h2hLobbyRole}
          startActor={h2hGetReadyData?.startActor}
          goalActor={h2hGetReadyData?.goalActor}
          savedGameId={activeSavedGameIdRef.current}
          onExit={() => handleReset('multiplayer')}
          onRefundTicket={(amount) => {
            const newCount = refundTicket(amount)
            setTickets(newCount)
            showToast('Ticket refunded!')
          }}
        />
      )
    }

    if (gameState === 'h2h-personal-results') {
      return (
        <H2HPersonalResults
          gameId={h2hLobbyGameId}
          result={h2hPersonalResult}
          opponentActor={h2hOpponentActor}
          onBothFinished={handleH2hBothFinished}
        />
      )
    }

    if (gameState === 'h2h-results-room') {
      return (
        <H2HResultsRoom
          gameData={h2hResultsGameData}
          myRole={h2hLobbyRole}
          tickets={displayTickets}
          onRematch={handleH2hRematch}
          onExit={handleH2hResultsExit}
          onOpenStore={openTicketStore}
        />
      )
    }

    if (gameState === 'h2h-pick') {
      return (
        <ActorPicker
          key={`${h2hRole}-${h2hPickStep}`}
          slotRole={h2hPickStep}
          goalActor={h2hPickStep === 'start' ? activeChallenge?.goalActor : null}
          onConfirm={handleH2hActorPicked}
          onCancel={handleH2hCancel}
        />
      )
    }

    if (gameState === 'h2h-code-reveal') {
      return (
        <ChallengeSent
          code={h2hGameCode}
          onPlayWhileWait={handlePlayWhileWait}
          onOpponentJoined={(ch) => {
            setActiveChallenge(ch)
            setGameState('h2h-reveal')
          }}
          onBack={() => setGameState('multiplayer')}
        />
      )
    }

    if (gameState === 'h2h-reveal') {
      return (
        <H2HReveal
          challenge={activeChallenge}
          myRole={h2hRole}
          onStartPlaying={h2hRole === 'p1' ? handlePlayWhileWait : null}
        />
      )
    }

    if (gameState === 'h2h-waiting') {
      return (
        <H2HWaiting
          challenge={activeChallenge}
          myRole={h2hRole}
          myResult={h2hMyResult}
        />
      )
    }

    if (gameState === 'h2h-abandoned') {
      return (
        <div className="abandoned-screen">
          <div className="abandoned-screen__inner">
            <div className="abandoned-screen__icon" aria-hidden="true"><IconTrophy size={48} /></div>
            <h2 className="abandoned-screen__title">Your opponent called cut!</h2>
            <p className="abandoned-screen__sub">You win by default! <IconClapper size={16} style={{ verticalAlign: 'middle' }} /></p>
            <p className="abandoned-screen__refund"><IconTicket size={14} style={{ verticalAlign: 'middle' }} /> Your ticket has been refunded.</p>
            <button
              type="button"
              className="btn-primary abandoned-screen__btn"
              onClick={() => handleReset('multiplayer')}
            >
              Back to Menu
            </button>
          </div>
        </div>
      )
    }

    if (gameState === 'h2h-results') {
      const ch = activeChallenge
      const handleRematch = () => {
        clearH2HState()
        setH2hMyResult(null)
        setH2hSyncedChallenge(null)
        setGameState('multiplayer')
      }
      return (
        <H2HResults
          challenge={ch}
          myRole={h2hRole}
          onRematch={handleRematch}
          onHome={() => { clearH2HState(); setH2hMyResult(null); setH2hSyncedChallenge(null); handleReset('home') }}
        />
      )
    }

    if (gameState === 'won') {
      return (
        <WinScreen
          chain={chain}
          startActor={startActor}
          endActor={endActor}
          moveCount={finalStats?.moves ?? moveCount}
          timeElapsed={finalStats?.time ?? elapsed}
          onPlayAgain={handlePlayAgain}
          onNewGame={handleNewGame}
          onGoHome={() => handleReset('home')}
          isDailyChallenge={isDailyChallenge}
          isHistoricalPuzzle={isHistoricalPuzzle}
          dailyNumber={dailyNumber}
          dailyDate={currentDailyDate}
          cueCardsUsed={cueCardsUsed}
          opponentForfeited={h2hOpponentForfeited}
          earnedTicket={isDailyChallenge}
          historicalPrevBest={isHistoricalPuzzle ? historicalPrevBest : null}
          isNewHistoricalBest={isHistoricalPuzzle ? isNewHistoricalBest : false}
          onViewStats={isDailyChallenge ? () => handleViewStats('won') : null}
        />
      )
    }

    // playing
    return renderPlaying()
  }

  const renderPlaying = () => {
    const chainActorIds = new Set(
      chain.filter((item) => item.type === 'actor').map((item) => item.id)
    )
    const opponentName = h2hRole === 'p1'
      ? (activeChallenge?.player2?.username || 'Opponent')
      : (activeChallenge?.player1?.username || 'Opponent')

    return (
    <div className="app">

      <div className="targets">
        <div className="target-card">
          <img
            src={startActor?.profile_path ? profileUrl(startActor.profile_path) : PLACEHOLDER_ACTOR}
            alt={startActor?.name}
            className="target-card__img"
          />
          <div className="target-card__info">
            <span className="target-card__label">START</span>
            <span className="target-card__name">{startActor?.name}</span>
          </div>
        </div>

        <div className="targets__divider"><IconArrowRight size={18} /></div>

        <div className="target-card target-card--end">
          <img
            src={endActor?.profile_path ? profileUrl(endActor.profile_path) : PLACEHOLDER_ACTOR}
            alt={endActor?.name}
            className="target-card__img"
          />
          <div className="target-card__info">
            <span className="target-card__label">GOAL</span>
            <span className="target-card__name">{endActor?.name}</span>
          </div>
        </div>
      </div>

      {(isDailyChallenge || isHistoricalPuzzle || activeChallenge?.gameId) && (
        <div className="game-mode-badge">
          {activeChallenge?.gameId
            ? <><IconSwords size={14} style={{ verticalAlign: 'middle' }} /> H2H vs {opponentName}</>
            : `Daily Challenge #${dailyNumber}`}
        </div>
      )}

      <Filmstrip chain={chain} onItemClick={handleFilmstripClick} followLiveEdge />

      <div className="context-bar">
        <div className="context-bar__text">
          {view === 'movies' ? (
            <p>Movies starring <strong>{currentActor?.name}</strong> — flip a poster to preview its cast</p>
          ) : (
            <p>Cast of <strong>{selectedMovie?.title}</strong> — flip an actor to see their resume</p>
          )}
        </div>
        <div className="context-bar__controls">
          {canGoBack && (
            <button className="btn-ghost" onClick={handleBack}>← Undo</button>
          )}
          {cueCardAvailable && activeGameType !== 'party-battle' && (
            <button
              className={`cue-pill${revealedHints.length > 0 ? ' cue-pill--used' : ''}`}
              onClick={handleCueCardClick}
              disabled={cueCardLoading}
            >
              {cueCardLoading ? 'Finding…' : (
                <>
                  <IconClapper size={16} />Cue Card
                  {revealedHints.length > 0 ? (
                    <span className="cue-pill__count">{revealedHints.length}</span>
                  ) : (
                    <span className="cue-pill__cost"><IconTicket size={11} />1</span>
                  )}
                </>
              )}
            </button>
          )}
          <button
            type="button"
            className="ctrl-pill ctrl-pill--btn ctrl-pill--danger"
            onClick={() => setQuitDialogOpen(true)}
          >
            <IconCut size={15} />Call Cut
          </button>
          <span className="ctrl-pill ctrl-pill--stat ctrl-pill--push">{moveCount} hops · {formatTime(elapsed)}</span>
        </div>
      </div>

      <main className="grid-area">
        {loading && (
          <div className={view === 'movies' ? 'movie-grid' : 'actor-grid'}>
            {Array.from({ length: view === 'movies' ? 8 : 9 }).map((_, i) => (
              <div key={i} className={`skeleton-card ${view === 'movies' ? 'skeleton-card--movie' : 'skeleton-card--actor'}`} />
            ))}
          </div>
        )}

        {error && (
          <div className="error-msg">
            <p>{error}</p>
            <button className="btn-primary" onClick={() => loadActorMovies(currentActor?.id)}>Retry</button>
          </div>
        )}

        {!loading && !error && view === 'movies' && (() => {
          const q = movieFilter.trim().toLowerCase()
          // No query → base grid is movies only. With a query → movies that
          // match PLUS the actor's TV shows that match (TV is searchable only).
          const filtered = q
            ? [
                ...items.filter((m) => (m.title || '').toLowerCase().includes(q)),
                ...tvItems.filter((t) => (t.title || '').toLowerCase().includes(q)),
              ]
            : items
          return (
            <>
              <div className="movie-filter-bar">
                <span className="movie-filter-bar__icon" aria-hidden="true">⌕</span>
                <input
                  ref={filterRef}
                  className="movie-filter-bar__input"
                  type="text"
                  placeholder="Search movies & TV…"
                  value={movieFilter}
                  onChange={(e) => setMovieFilter(e.target.value)}
                  aria-label="Search movies and TV shows by title"
                />
                {movieFilter && (
                  <>
                    <span className="movie-filter-bar__count">
                      {filtered.length} found
                    </span>
                    <button
                      className="movie-filter-bar__clear"
                      onClick={() => { setMovieFilter(''); filterRef.current?.focus() }}
                      aria-label="Clear filter"
                    >
                      <IconX size={14} />
                    </button>
                  </>
                )}
              </div>

              {filtered.length > 0 ? (
                <div className="movie-grid">
                  {filtered.map((movie) => (
                    <FlipMovieCard
                      key={`${movie.mediaType || 'movie'}-${movie.id}`}
                      movie={movie}
                      onExpand={handleExpand}
                    />
                  ))}
                </div>
              ) : (
                <p className="movie-filter-bar__empty">
                  No movies or shows match "<strong>{movieFilter}</strong>"
                </p>
              )}
            </>
          )
        })()}

        {!loading && !error && view === 'cast' && (
          <div className="actor-grid">
            {items.map((actor) => {
              const isActorTarget = settings.destinationHighlight && actor.id === endActor?.id
              return (
                <FlipActorCard
                  key={actor.id}
                  actor={actor}
                  onExpand={handleExpand}
                  isTarget={isActorTarget}
                  isInChain={!isActorTarget && chainActorIds.has(actor.id)}
                />
              )
            })}
          </div>
        )}
      </main>

      {expandedCard && (
        <CardOverlay
          key={`${expandedCard.type}-${expandedCard.data.id}`}
          type={expandedCard.type}
          data={expandedCard.data}
          startRect={expandedCard.rect}
          isTarget={settings.destinationHighlight && expandedCard.isTarget}
          onSelect={handleOverlaySelect}
          onDismiss={() => setExpandedCard(null)}
        />
      )}

      {pendingJumpIndex != null && (
        <div className="jump-dialog-overlay" onClick={handleCancelJump}>
          <div className="jump-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="jump-dialog__msg">
              Editing this step will clear <strong>{hopsAhead} {hopsAhead === 1 ? 'hop' : 'hops'}</strong> ahead. Are you sure?
            </p>
            <div className="jump-dialog__actions">
              <button className="btn-primary" onClick={handleConfirmJump}>Make the Edit</button>
              <button className="btn-ghost" onClick={handleCancelJump}>Stay on Set</button>
            </div>
          </div>
        </div>
      )}

      {/* ── H2H Forfeit popup overlay ── */}
      {h2hForfeitInfo && (
        <div className="forfeit-popup-overlay">
          <div className="forfeit-popup">
            {h2hForfeitInfo.forfeitedAt0Hops ? (
              <>
                <p className="forfeit-popup__title">Game Cancelled</p>
                <p className="forfeit-popup__msg">Your opponent left before making any moves.</p>
                <p className="forfeit-popup__refund"><IconTicket size={14} style={{ verticalAlign: 'middle' }} /> Your ticket has been refunded.</p>
                <div className="forfeit-popup__actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => { setH2hForfeitInfo(null); handleReset('home') }}
                  >
                    Return to Trailer
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="forfeit-popup__title">Opponent Forfeited!</p>
                <p className="forfeit-popup__msg">Your opponent called cut and left the game.</p>
                <p className="forfeit-popup__refund"><IconTicket size={14} style={{ verticalAlign: 'middle' }} /> Your ticket has been refunded.</p>
                <div className="forfeit-popup__actions">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => setH2hForfeitInfo(null)}
                  >
                    Keep Rolling <IconClapper size={15} style={{ verticalAlign: 'middle' }} />
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => { setH2hForfeitInfo(null); handleReset('home') }}
                  >
                    Return to Trailer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Cue Card result slide-up ── */}
      {(cueCardLoading || cueCardResult) && (
        <div className="cue-card-overlay" onClick={() => !cueCardLoading && setCueCardResult(null)}>
          <div className="cue-card-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="cue-card-sheet__header">
              <span className="cue-card-sheet__title"><IconCueCard size={15} style={{ verticalAlign: 'middle' }} /> Cue Card</span>
              {!cueCardLoading && (
                <button
                  type="button"
                  className="cue-card-sheet__close"
                  onClick={() => setCueCardResult(null)}
                  aria-label="Dismiss"
                ><IconX size={16} /></button>
              )}
            </div>

            {cueCardLoading && (
              <div className="cue-card-sheet__loading">
                <span className="cue-card-sheet__spinner" aria-hidden="true">⟳</span>
                <span>Finding your hint…</span>
              </div>
            )}

            {/* ── SPOILER WARNING ── */}
            {!cueCardLoading && cueCardResult?.type === 'spoiler' && (
              <div className="cue-card-sheet__body cue-card-sheet__body--spoiler">
                <p className="cue-card-sheet__spoiler-title"><IconTarget size={16} style={{ verticalAlign: 'middle' }} /> Spoiler Warning!</p>
                <p className="cue-card-sheet__spoiler-desc">
                  The hint reveals a direct path to <strong>{endActor?.name}</strong>.
                  Peeking will cost your ticket.
                </p>
                <div className="cue-card-sheet__spoiler-actions">
                  <button
                    type="button"
                    className="btn-primary cue-card-sheet__reveal-btn"
                    onClick={() => setCueCardResult(cueCardResult.hiddenResult)}
                  >
                    Reveal Anyway <IconCueCard size={15} style={{ verticalAlign: 'middle' }} />
                  </button>
                  <button
                    type="button"
                    className="btn-ghost cue-card-sheet__refund-btn"
                    onClick={() => {
                      setTickets((t) => t + 1)
                      setCueCardsUsed((h) => Math.max(0, h - 1))
                      showToast('Ticket refunded — you\'ve got this!')
                      setCueCardResult(null)
                    }}
                  >
                    I'll figure it out
                  </button>
                </div>
              </div>
            )}

            {/* ── NORMAL HINT ── */}
            {!cueCardLoading && cueCardResult?.type === 'hint' && (() => {
              const tempConfig = {
                hot:              { Icon: IconFlame,       color: '#ff5436', phrase: 'Burning hot — one hop away!' },
                warm:             { Icon: IconThermometer, color: '#ff8a3d', phrase: 'Very warm — almost there!' },
                'getting-warmer': { Icon: IconThermometer, color: '#f5b53d', phrase: 'Getting warmer…' },
                freezing:         { Icon: IconSnowflake,   color: '#4ea6ff', phrase: 'Freezing — long road ahead' },
              }
              const { Icon: TempIcon, color: tempColor, phrase } = tempConfig[cueCardResult.temperature] ?? tempConfig.freezing
              const actorImg = cueCardResult.actor?.profile_path
                ? profileUrl(cueCardResult.actor.profile_path)
                : PLACEHOLDER_ACTOR
              const posterSrc = cueCardResult.movie?.poster_path
                ? posterUrl(cueCardResult.movie.poster_path, 'w92')
                : null
              return (
                <div className="cue-card-sheet__body">
                  <div className={`cue-card-sheet__temp cue-card-sheet__temp--${cueCardResult.temperature}`}>
                    <TempIcon size={18} color={tempColor} style={{ verticalAlign: 'middle' }} /> {phrase}
                  </div>
                  <p className="cue-card-sheet__hint">
                    <strong>{currentActor?.name}</strong> was in{' '}
                    <em>"{cueCardResult.movie?.title}"</em> with{' '}
                    <strong>{cueCardResult.actor?.name}</strong>.
                  </p>
                  {posterSrc && (
                    <img className="cue-card-sheet__poster" src={posterSrc} alt={cueCardResult.movie?.title} />
                  )}
                  <img className="cue-card-sheet__photo" src={actorImg} alt={cueCardResult.actor?.name} />
                  <p className="cue-card-sheet__name">{cueCardResult.actor?.name}</p>
                  <button type="button" className="btn-primary cue-card-sheet__dismiss" onClick={() => setCueCardResult(null)}>
                    Got it! <IconCueCard size={15} style={{ verticalAlign: 'middle' }} />
                  </button>
                </div>
              )
            })()}

            {/* ── NO RESULT / ERROR ── */}
            {!cueCardLoading && (cueCardResult?.type === 'none' || cueCardResult?.type === 'error') && (
              <div className="cue-card-sheet__body">
                <p className="cue-card-sheet__hint cue-card-sheet__hint--no-result">
                  Even our best scouts couldn't find a connection right now.{' '}
                  <strong>Your ticket has been refunded.</strong>
                </p>
                <button type="button" className="btn-primary cue-card-sheet__dismiss" onClick={() => setCueCardResult(null)}>
                  Got it! <IconCueCard size={15} style={{ verticalAlign: 'middle' }} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    )
  }

  return (
    <>
      <GlobalHeader
        user={user}
        tickets={displayTickets}
        onMenuToggle={() => setMenuOpen((o) => !o)}
        menuOpen={menuOpen}
        onOpenStore={openTicketStore}
        partyHostMode={partyRole === 'host' || isHostPopup}
        showLogo={gameState !== 'home'}
      />

      <div className="app-body">
        {renderBody()}
      </div>

      <SideMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onNavigate={navigate}
        user={user}
        currentUser={currentUser}
        currentStreak={streak?.currentStreak ?? 0}
        activeState={gameState}
      />

      {saveExitOpen && (
        <SaveExitModal
          onSaveExit={handleSaveExit}
          onStay={handleStayOnSet}
          body={gameState === 'setup'
            ? "You've already paid for this game. Leave now and your tickets won't be refunded."
            : (isDailyChallenge || isHistoricalPuzzle)
            ? "You're in the middle of today's puzzle. Want to save and come back?"
            : undefined}
        />
      )}

      {hostExitConfirmOpen && (
        <div className="host-exit-overlay" onClick={dismissHostExit}>
          <div className="host-exit-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="host-exit-dialog__title">End the Party?</p>
            <p className="host-exit-dialog__body">Leaving will end the party for all players.</p>
            <div className="host-exit-dialog__actions">
              <button className="btn-ghost" onClick={dismissHostExit}>
                Stay <IconClapper size={15} style={{ verticalAlign: 'middle' }} />
              </button>
              <button className="btn-primary" onClick={confirmHostExitAndEnd}>
                End Party <IconMasks size={15} style={{ verticalAlign: 'middle' }} />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`toast ${toastMsg ? 'toast--visible' : ''}`}>{toastMsg || ''}</div>

      {ticketStoreOpen && (
          <TicketStore
            tickets={displayTickets}
            onPurchase={handlePurchase}
            onClose={() => setTicketStoreOpen(false)}
          />
        )}

      {signInOpen && (
        <SignIn
          message={signInMessage}
          onClose={() => { setSignInOpen(false); setSignInMessage(null) }}
          onSignedIn={(user) => handleUserSignedIn(user)}
        />
      )}

      {/* ── Quit ("Call Cut") confirmation dialog ── */}
      {quitDialogOpen && (() => {
        const isH2H = !!h2hRole && !!h2hLobbyGameId && gameState === 'playing'
        const activeEntry = activeSavedGameIdRef.current
          ? getSavedGameById(activeSavedGameIdRef.current)
          : null

        if (isH2H) {
          // Mirror the same refundEligible logic used in handleConfirmQuit
          const isH2H0Hops = activeEntry ? (activeEntry.refundEligible === true) : (moveCount === 0)
          return (
            <div className="quit-overlay" onClick={() => setQuitDialogOpen(false)}>
              <div className="quit-dialog" onClick={(e) => e.stopPropagation()}>
                <p className="quit-dialog__title">{isH2H0Hops ? <><IconCut size={18} />Call Cut?</> : 'Forfeit?'}</p>
                <p className="quit-dialog__body">
                  {isH2H0Hops
                    ? 'Leaving now will end the game for both players.'
                    : 'Leaving now will forfeit the game.'}
                </p>
                {isH2H0Hops
                  ? <p className="quit-dialog__refund"><IconTicket size={13} /> You'll get your ticket back — no moves made yet.</p>
                  : <p className="quit-dialog__refund"><IconTicket size={13} /> No refund — forfeiting costs your ticket.</p>
                }
                <div className="quit-dialog__actions">
                  <button
                    type="button"
                    className="btn-ghost quit-dialog__stay"
                    onClick={() => setQuitDialogOpen(false)}
                  >
                    Stay on Set
                  </button>
                  <button
                    type="button"
                    className="btn-primary quit-dialog__confirm"
                    onClick={handleConfirmQuit}
                  >
                    {isH2H0Hops ? 'Call Cut' : 'Forfeit'}
                  </button>
                </div>
              </div>
            </div>
          )
        }

        // ── Single / Daily / Historical dialog ──
        const isSaveExit = activeEntry !== null && !activeEntry?.refundEligible
        return (
          <div className="quit-overlay" onClick={() => setQuitDialogOpen(false)}>
            <div className="quit-dialog" onClick={(e) => e.stopPropagation()}>
              <p className="quit-dialog__title"><IconCut size={18} />Call Cut?</p>
              <p className="quit-dialog__body">Are you sure you want to call cut?</p>
              {activeEntry?.refundEligible
                ? <p className="quit-dialog__refund"><IconTicket size={13} /> You'll get your ticket back — no hops made yet.</p>
                : <>
                    <p className="quit-dialog__refund"><IconTicket size={13} /> No refund — you've already made progress.</p>
                    {isSaveExit && (
                      <p className="quit-dialog__save-note"><IconSave size={13} /> Your game will be saved and you can resume it anytime from the home screen.</p>
                    )}
                  </>
              }
              <div className="quit-dialog__actions">
                <button
                  type="button"
                  className="btn-ghost quit-dialog__stay"
                  onClick={() => setQuitDialogOpen(false)}
                >
                  Stay on Set
                </button>
                <button
                  type="button"
                  className="btn-primary quit-dialog__confirm"
                  onClick={handleConfirmQuit}
                >
                  {isSaveExit ? <><IconSave size={15} />Save &amp; Exit</> : 'Call Cut'}
                </button>
              </div>
            </div>
          </div>
        )
      })()}

      {cueCardDialogOpen && (
        <div className="cue-card-confirm-overlay" onClick={() => setCueCardDialogOpen(false)}>
          <div className="cue-card-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <p className="cue-card-confirm-dialog__title">Cue Card</p>
            <p className="cue-card-confirm-dialog__msg">
              Reveal a hint connecting <strong>{currentActor?.name}</strong> to{' '}
              <strong>{endActor?.name}</strong>.
            </p>
            <p className="cue-card-confirm-dialog__cost">
              <IconTicket size={13} aria-hidden="true" />
              <span>1 Ticket · You have {tickets.toLocaleString()}</span>
            </p>
            {tickets >= 1 ? (
              <div className="cue-card-confirm-dialog__actions">
                <button
                  className="btn-primary"
                  onClick={handleConfirmCueCard}
                  disabled={cueCardProcessingRef.current}
                >
                  {revealedHints.length > 0 ? 'Reveal Another' : 'Use Cue Card'}
                </button>
                <button className="btn-ghost" onClick={() => setCueCardDialogOpen(false)}>
                  Back to Set
                </button>
              </div>
            ) : (
              <div className="cue-card-confirm-dialog__actions">
                <p className="cue-card-confirm-dialog__broke">Not enough tickets</p>
                <button className="btn-primary" onClick={() => { setCueCardDialogOpen(false); setTicketStoreOpen(true) }}>
                  Get Tickets
                </button>
                <button className="btn-ghost" onClick={() => setCueCardDialogOpen(false)}>
                  Back to Set
                </button>
              </div>
            )}

            {revealedHints.length > 0 && (
              <div className="cue-tray">
                <div className="cue-tray__label">
                  <IconCheck size={13} /> {revealedHints.length} Cue{revealedHints.length > 1 ? 's' : ''} Revealed
                </div>
                {revealedHints.length < 4 ? (
                  <div className="cue-tray__row">
                    {revealedHints.map((h) => (
                      <CueHintThumb key={h.movie?.id} hint={h} onView={() => reviewHint(h)} />
                    ))}
                  </div>
                ) : (
                  <div className="cue-tray__reel">
                    <div className="cue-tray__sprockets" aria-hidden="true">
                      {Array.from({ length: 14 }).map((_, i) => <i key={i} />)}
                    </div>
                    <div className="cue-tray__reel-track">
                      {revealedHints.map((h) => (
                        <CueHintThumb key={h.movie?.id} hint={h} onView={() => reviewHint(h)} />
                      ))}
                    </div>
                    <div className="cue-tray__sprockets" aria-hidden="true">
                      {Array.from({ length: 14 }).map((_, i) => <i key={i} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
